// /brands/new — Brand-onboarding wizard. Layout follows the /onboarding
// prototype design language (3-column brand review, 2x3 competitor grid,
// per-advertiser ads sections, large winning-ideas placeholders) while
// preserving the existing 8-step state machine + all async flows
// (auto-discovery of products on brand step, suggestCompetitors fan-out,
// runAdStrategist on opening, sessionStorage hand-off to /gallery).
//
// Step machine (unchanged):
//   url → gather → brand → find-competitors → pick-competitors →
//   competitor-ads → find-opening → review
//
// The progress bar visually collapses find-competitors + pick-competitors
// into one bar segment (a load-then-pick pair) — 7 segments total for 8
// steps, see getProgressSegmentIndex.
//
// Loading steps use the shared WizardLoadingStep so animation styling
// stays consistent with the rest of the wizard family.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  scrapeBrand,
  createBrand,
  patchBrand,
  activateBrand,
  discoverProducts,
  uploadBrandImage,
  uploadProduct,
  ProductNeedsInfoError,
  saveProducts,
  saveProductsAsync,
  getProductSaveJobStatus,
  listProducts,
  startBrandSetup,
  startBrandImagery,
  productLimitForTier,
  suggestCompetitors,
  fetchCompetitorAdsByQuery,
  addCompetitor,
  addCompetitorPage,
  getBrandSetupPreview,
  checkCompetitorsCache,
  type BrandFontEntry,
  type BrandRecord,
  type UserProduct,
  type CompetitorSuggestion,
  type BrandSetupTheme,
  type BrandSetupWinningIdea,
  writeBrandSetupConcepts,
  buildBrandDna,
  scrapeBrandImagery,
  InsufficientCreditsError,
} from '@/lib/api';
import { useCredits } from '@/hooks/useCredits';
import { useConfirm } from '@/hooks/useConfirm';
import { track } from '@/lib/analytics';
import WizardLoadingStep from './components/WizardLoadingStep';
import { Logo } from '@/components/feature/Logo';
import { MeshBg } from '@/components/feature/MeshBg';

type StepId =
  | 'url'
  | 'gather'
  | 'brand'
  | 'save-products'        // Jun 15 — dedicated blocking step for the async product save
  | 'competitors-intro'    // Jun 21 — Continue/Skip gate: "we'll find your competitors"
  | 'find-competitors'
  | 'pick-competitors'
  | 'ads-intro'            // Jul 17 — 2nd gate: "we'll pull their live ads (a few mins)"
  | 'competitor-ads'
  | 'pick-featured-products'  // Jun 15 — product picker BEFORE find-opening so concepts can be product-aware
  | 'find-opening'
  | 'review';

// 5 ads charged + rendered upfront. Backend also inserts 15 STUB posts
// (no image, render_config attached with the LLM concept). Each stub
// shows as a locked tile in /gallery — user clicks Unlock to fire the
// render on-demand for 1 credit. Total potential cost: 5 upfront + 15
// pay-as-you-go = 20 credits if user unlocks everything, vs 20 upfront
// before. Saves ~80% of users who don't want all 20.
const TARGET_COUNT = 5;

// Jul 26 — cap the FEATURED product selection (the set that feeds the concept
// writer + 20-gen). With many products the writer stitches copy across them
// (a "routine" headline mashing several items), so limit to 4. This is the
// featured/generation set only — the brand-step SAVE selection stays uncapped
// (the whole catalog still lands in My Products).
const MAX_FEATURED_PRODUCTS = 4;

// 7 visual progress segments for 9 StepIds — find-competitors and
// pick-competitors share one segment. save-products folds into the
// brand segment (it's a follow-on of "set up your brand").
const PROGRESS_TOTAL = 7;

function getProgressSegmentIndex(step: StepId): number {
  switch (step) {
    case 'url':                       return 0;
    case 'gather':                    return 1;
    case 'brand':                     return 2;
    case 'save-products':             return 2;  // same segment as brand
    case 'competitors-intro':         return 3;  // gate — shares the competitor segment
    case 'find-competitors':          return 3;
    case 'pick-competitors':          return 3;
    case 'ads-intro':                 return 4;  // ads gate — shares the ads segment
    case 'competitor-ads':            return 4;
    case 'pick-featured-products':    return 5;  // new step, replaces find-opening's slot
    case 'find-opening':              return 5;
    case 'review':                    return 6;
  }
}

interface CompetitorAdsBundle {
  query: string;
  label: string;
  ads: {
    media_cached_url?: string | null;
    media_ref?: string | null;
    platforms?: string[];
    longevity_days?: number;
  }[];
}

// In-memory representation of everything the brand-review screen displays
// and edits. Nothing here is persisted until the user clicks "Continue" on
// the brand step (the single createBrand → patchBrand → activateBrand →
// saveProducts commit point in `handlePickContinue`). Means a wizard that
// gets abandoned before then leaves no DB rows AND consumes no plan quota
// — important because the plan caps brand count per tier.
interface BrandDraft {
  name: string;
  brief: string;
  colors: string[];
  logoBase64: string | null;
  sourceUrl: string | null;
  /** Jun 22 — favicon URL from scrape (separate from logo, optional). */
  faviconUrl: string | null;
  /** Jul 11 — brand imagery URLs (campaign / lifestyle shots) from the scrape,
   *  shown in the review "Images" section. May be empty. */
  images: string[];
  /** @deprecated Flat list of detected font names — kept for back-compat
   *  with the older brand-review render. New shape is `fonts` below. */
  typography: string[];
  /** Jun 15 — Google-Fonts-substituted entries. Each pairs the detected
   *  font with the Google Font we'll actually render with. */
  fonts: BrandFontEntry[];
}

// Common TLDs accepted by the URL field. Catches typos like ".coim" while
// staying lenient for niche TLDs (any 2-letter TLD = country code, accepted
// without listing). This is a sanity heuristic — not a registry — so if a
// legit site uses something exotic we can extend the list. Used only by
// `isValidWebsiteUrl` below.
const COMMON_TLDS = new Set([
  // generic
  'com', 'net', 'org', 'io', 'co', 'ai', 'app', 'dev', 'store', 'shop', 'biz',
  'info', 'me', 'xyz', 'tech', 'online', 'site', 'website', 'blog', 'design',
  'studio', 'agency', 'digital', 'media', 'news', 'cloud', 'tools', 'space',
  'world', 'life', 'live', 'today', 'works', 'art', 'press', 'company',
  'group', 'plus', 'pro', 'solutions', 'services', 'marketing', 'events',
  'center', 'community', 'gallery', 'beauty', 'fashion', 'health', 'fitness',
  'food', 'restaurant', 'cafe', 'bar', 'wine', 'coffee', 'shopping', 'eco',
  'green', 'global', 'international', 'inc', 'llc', 'ltd', 'name', 'family',
  'eu', 'asia',
  // common TLDs sometimes mistyped — listing them explicitly so the typo
  // check (the implicit "not in set") catches the close-miss variant.
  'mil', 'gov', 'edu',
]);

// Sanity-check the URL the user pasted before firing the scrape job. Catches
// typos (`nike.coim`) + outright garbage (`asdfasdf`) without making us
// reach for a full TLD registry. Country-code TLDs (2 letters) accepted
// implicitly. Anything 3+ chars must be in COMMON_TLDS. Returns `null` when
// valid, otherwise a short user-facing error string.
// Jun 16 — wizard timing helper. Wraps an awaitable in a Date.now()
// bracket and logs to console with a consistent prefix so the user can
// open DevTools, reproduce a slow onboarding, and have a clean elapsed
// breakdown per API call. Captures cold-start spikes too (since the
// timer includes network + Cloud Run startup, not just backend work).
// The console.log keeps the original return value untouched.
async function logTiming<T>(label: string, fn: () => Promise<T>): Promise<T> {
  const t0 = Date.now();
  // eslint-disable-next-line no-console
  console.log(`[wizard-timing] ${label} → starting`);
  try {
    const result = await fn();
    // eslint-disable-next-line no-console
    console.log(`[wizard-timing] ${label} → DONE in ${Date.now() - t0}ms`);
    return result;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(
      `[wizard-timing] ${label} → FAILED after ${Date.now() - t0}ms: ` +
      `${err instanceof Error ? err.message : String(err)}`,
    );
    throw err;
  }
}


function validateWebsiteUrl(raw: string): string | null {
  const v = raw.trim();
  if (!v) return 'Paste your brand website URL.';
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
  } catch {
    return 'That doesn’t look like a valid URL.';
  }
  const host = url.hostname.toLowerCase();
  if (!host.includes('.') || /\s|\.\.|^\.|\.$/.test(host)) {
    return 'That doesn’t look like a valid website.';
  }
  const labels = host.split('.');
  for (const label of labels) {
    if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label)) {
      return 'That doesn’t look like a valid website.';
    }
  }
  const tld = labels[labels.length - 1];
  if (/^[a-z]{2}$/.test(tld)) return null;       // country code — accepted
  if (COMMON_TLDS.has(tld)) return null;
  return `Hmm, “.${tld}” doesn’t look like a real TLD. Did you mean .com?`;
}

export default function NewBrandWizardPage() {
  const navigate = useNavigate();
  const { balance, planTier } = useCredits();
  const confirm = useConfirm();

  const [step, setStep] = useState<StepId>('url');
  const startedRef = useRef<Set<string>>(new Set());

  // ── PostHog: the onboarding drop-off funnel ────────────────────────────
  // Fires on EVERY wizard step (covers advance / advanceWithGuard / any
  // setStep). Build a PostHog funnel on this event's `step` property in
  // wizard order to see exactly where cold traffic abandons brand setup.
  useEffect(() => {
    track('brand_setup_step', {
      step,
      segment: getProgressSegmentIndex(step) + 1, // 1-based, human readable
      segment_total: PROGRESS_TOTAL,
    });
  }, [step]);

  const [websiteUrl, setWebsiteUrl] = useState('');
  // Local working copy of the brand identity the user is reviewing/editing on
  // the brand step. Populated by `runGather` (scrape) or `handleManualEntry`
  // (URL-seeded defaults). NOT persisted — `handlePickContinue` is the single
  // commit point that writes to the backend.
  const [brandDraft, setBrandDraft] = useState<BrandDraft | null>(null);
  // Real backend row, populated AFTER `handlePickContinue` succeeds. Until
  // then null — competitors / opening / review steps assume it exists, but
  // they only run after the brand commit so that's safe.
  const [createdBrand, setCreatedBrand] = useState<BrandRecord | null>(null);
  // Synchronous mirror of createdBrand — image upload (which persists
  // immediately) may lazily create the brand mid-review, and handlePickContinue
  // must see that within the same tick to avoid a duplicate createBrand.
  const createdBrandRef = useRef<BrandRecord | null>(null);
  // IDs of products saved via manual image UPLOAD (already in user_products).
  // Excluded from the Continue-time saveProductsAsync so they aren't re-saved.
  const savedUploadIdsRef = useRef<Set<string>>(new Set());
  const [products, setProducts] = useState<UserProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [productSearch, setProductSearch] = useState('');
  const [suggestions, setSuggestions] = useState<CompetitorSuggestion[]>([]);
  // Jun 15 — Skip during competitor analysis now ACTUALLY cancels the
  // backend work via AbortController, not just discards the result.
  // The previous "let the request finish + ignore it" path burned
  // ~$0.001 in LLM cost + dozens of httpx connect-timeout retries per
  // skip (FB_VERIFY hits on eighthdayskin.com / defenage.com / etc.,
  // each ~10s of dead time). Real cancellation means fetch() aborts +
  // the FastAPI request returns immediately + no more LLM tokens get
  // generated server-side.
  const competitorsAbortRef = useRef<AbortController | null>(null);
  const adsAbortRef = useRef<AbortController | null>(null);
  // Kept for the result-commit guards (skipped runners shouldn't
  // touch state if their AbortController-cancelled request races to
  // resolve before the abort propagates).
  const competitorsSkippedRef = useRef(false);
  const adsSkippedRef = useRef(false);
  const [selectedCompetitors, setSelectedCompetitors] = useState<Set<string>>(new Set());
  const [competitorAds, setCompetitorAds] = useState<CompetitorAdsBundle[]>([]);
  // Tracks which competitor queries have FINISHED fetching (regardless of
  // whether they returned ads or not). Used to (a) show a per-section
  // loading state while pending, and (b) hide sections that returned zero
  // ads. Used for the re-entry check (don't re-fire runLoadAds when user
  // backs into the step). Populated atomically at end of runLoadAds —
  // we no longer stream per-competitor results to the UI, so an in-flight
  // load shows the unified WizardLoadingStep instead of mixed pending/loaded.
  const [adsFetchedQueries, setAdsFetchedQueries] = useState<Set<string>>(new Set());
  const [adsLoading, setAdsLoading] = useState(false);
  const [openingBrief, setOpeningBrief] = useState<string | null>(null);
  // 5 themed cards on the review step (Premium / Warm & lifestyle / Playful
  // / Clean & minimal / Editorial — mirrors BRAND_SETUP_PLAN_SLOTS vibe
  // families). Populated by runFindOpening from /api/brand-setup/preview.
  const [themes, setThemes] = useState<BrandSetupTheme[]>([]);
  // Jun 15 — product-aware winning ideas from /write-concepts. Replaces
  // the brand-generic vibe themes that didn't match rendered ads. Each
  // winning idea is the first concept of one of the 5 angle buckets;
  // the 20-slot plan that ships back to /start uses the same concepts
  // so what the user saw IS what gets rendered.
  const [winningIdeas, setWinningIdeas] = useState<BrandSetupWinningIdea[]>([]);
  // 20-slot plan returned by /api/brand-setup/preview, with concepts
  // pre-built per slot. Passed back to /start so the worker skips the
  // 20 per-slot LLM concept calls that previously fired during render.
  // Opaque to the frontend — just stored + forwarded.
  const [cachedPlan, setCachedPlan] = useState<unknown[] | null>(null);

  const [stepError, setStepError] = useState<Partial<Record<StepId, string>>>({});
  const [generating, setGenerating] = useState(false);
  const [savingProducts, setSavingProducts] = useState(false);
  // Jun 17 — flips true during advanceWithGuard's pick-featured-products →
  // find-opening transition while we sync-save any product picks the bg
  // job hasn't reached yet (typically 0-2 stragglers, ~3-8s). Drives the
  // Continue button's "Importing products…" label so it doesn't sit
  // there silent.
  const [resolvingFeatured, setResolvingFeatured] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);

  // Jun 15 — Async product-save tracking. saveProductsAsync returns
  // instantly with a job_id; the actual DB writes run in a background
  // worker that may take several minutes for a large batch (359-product
  // import = ~5-10 min at Semaphore(20) parallel). Without polling, the
  // user reaches the review step + clicks Generate BEFORE any product
  // row exists for the new brand → backend's
  // _fetch_brand_products_for_setup returns 0 → "Add at least one
  // product" error. We poll the job's status and gate Generate on it.
  const [productSaveJobId, setProductSaveJobId] = useState<string | null>(null);
  const [productSaveStatus, setProductSaveStatus] = useState<{
    status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
    processed: number;
    target: number;
  } | null>(null);

  // Jun 15 — featured product picker on the review step. User sub-selects
  // which of their picked-during-brand products to feature in the 5 ads.
  // Empty = nothing picked yet (Generate is disabled until user opens
  // the modal + confirms a selection). Modal opens via the bottom CTA;
  // confirming the selection in the modal flips the CTA from "Select
  // products to generate ads" → "Generate ads".
  const [featuredProductIds, setFeaturedProductIds] = useState<string[]>([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  // Jul 12 — pending manual product. Set when a user-uploaded product image
  // couldn't be identified by vision (backend 422 needs_manual_info). We do
  // NOT save it until the user supplies a name + brief — we can't advertise a
  // product we can't identify (it would leak into a cross-category recipe).
  const [manualProduct, setManualProduct] = useState<{
    file: File; previewUrl: string; name: string; description: string;
    saving: boolean; error: string | null;
  } | null>(null);
  // True when the user clicked "Continue manually" after a scrape failure.
  // Suppresses the product-discovery auto-fire on the brand step (no usable
  // URL to scrape) so the user sees a clean empty review screen with
  // dashed/empty sections to fill in by hand.
  const [enteredManually, setEnteredManually] = useState(false);

  const advance = (next: StepId) => setStep(next);

  // Guarded advance — special-cases the pick-competitors → competitor-ads
  // transition by running a fast cache-only check first. Competitors with
  // confirmed zero Meta ads (cache hit returning '[]') get dropped from
  // the selection BEFORE the competitor-ads step starts loading. Cuts the
  // worst-case wait pattern ("7 picked, 6 timed out with no ads, 1 showed
  // up"). 'unknown' status (cache miss) stays in — we try Apify with a
  // 20s timeout; pre-cache from suggest-competitors should have warmed
  // most queries by now.
  const advanceWithGuard = async (next: StepId) => {
    if (next === 'competitor-ads') {
      const picked = suggestions.filter(s => selectedCompetitors.has(s.query));
      if (picked.length > 0) {
        try {
          const checks = await checkCompetitorsCache(picked.map(p => p.query));
          const noAdsQueries = new Set(
            checks.filter(c => c.status === 'no-ads').map(c => c.query),
          );
          if (noAdsQueries.size > 0) {
            console.log(
              '[wizard] dropping', noAdsQueries.size,
              'confirmed-no-ad competitors before competitor-ads:',
              [...noAdsQueries],
            );
            setSelectedCompetitors(prev => {
              const n = new Set<string>();
              for (const q of prev) if (!noAdsQueries.has(q)) n.add(q);
              return n;
            });
          }
        } catch (e) {
          console.error('[wizard] cache-check failed (continuing):', e);
        }
      }
    }
    // Jun 17 — when entering find-opening from pick-featured-products,
    // make sure the picked products are SAVED with real DB UUIDs before
    // /write-concepts fires. The brand-step bg-save (handlePickContinue)
    // typically finishes well before this point, but if the user blazed
    // through find-competitors + competitor-ads OR the bg save errored,
    // some picks may still have source_url-shaped ids. Resolve via:
    //   1. listProducts() to map source_url → UUID for already-saved picks
    //   2. For unsaved picks, synchronous /api/products/save-batch (5
    //      products = ~3-8s with Pillow + classify + GCS + DB)
    //   3. Replace featuredProductIds with the real UUIDs
    // Skipped silently if everything's already UUID-shaped.
    if (next === 'find-opening' && featuredProductIds.length > 0) {
      const isUuid = (s: string) => /^[0-9a-f-]{36}$/.test(s);
      const allUuids = featuredProductIds.every(isUuid);
      if (!allUuids) {
        setResolvingFeatured(true);
        try {
          // Look up which discovered products were picked. `products`
          // state holds the discovered list (source_url as id) until
          // the save-products polling effect overwrites it post-save.
          // Skip already-saved (UUID-shaped) ids — e.g. image-uploaded
          // products, which are persisted up front and have no source_url to
          // remap on (re-saving them would create a duplicate).
          const pickedDiscovered = products.filter((p) =>
            featuredProductIds.includes(p.id) && !isUuid(p.id),
          );
          if (pickedDiscovered.length > 0) {
            // Pull the latest saved list — bg save may have finished by
            // now, in which case some picks already have real UUIDs.
            const savedList = await logTiming(
              'listProducts (post-pick UUID resolve)',
              () => listProducts(),
            );
            const savedBySource = new Map(savedList.map((p) => [p.sourceUrl ?? '', p]));
            const needSync: typeof pickedDiscovered = [];
            const resolvedIds: string[] = [];
            for (const dp of pickedDiscovered) {
              const match = savedBySource.get(dp.sourceUrl ?? dp.id);
              if (match) {
                resolvedIds.push(match.id);
              } else {
                needSync.push(dp);
              }
            }
            if (needSync.length > 0) {
              // The bg save hasn't reached these — fast-save them
              // synchronously. ~3-8s for 5 products. saveProducts() reads
              // .sourceUrl / .name / .description / .imageUrl off each
              // entry, so the discovered-list shape (which has those)
              // works as-is even though some other UserProduct fields
              // may be empty.
              const fastSaved = await logTiming(
                `saveProducts (sync fast-save ${needSync.length} stragglers)`,
                () => saveProducts(needSync),
              );
              for (const fp of fastSaved.added) resolvedIds.push(fp.id);
            }
            if (resolvedIds.length > 0) {
              setFeaturedProductIds(resolvedIds);
            }
          }
        } catch (e) {
          console.error('[wizard] featured-product UUID resolve failed:', e);
          // Don't block — /write-concepts will fail cleanly if the ids
          // are bogus; the user sees the error on the find-opening step.
        } finally {
          setResolvingFeatured(false);
        }
      }
    }
    advance(next);
  };
  const setErr = (id: StepId, msg: string) =>
    setStepError(prev => ({ ...prev, [id]: msg }));
  const clearErr = (id: StepId) =>
    setStepError(prev => ({ ...prev, [id]: undefined }));

  // Jun 15 — Poll the product-save job every 3s until terminal. Stops
  // automatically when status flips to completed/failed/cancelled. The
  // review step + Generate-button gating read from productSaveStatus.
  useEffect(() => {
    if (!productSaveJobId) return;
    // Don't poll if we already know the job hit a terminal state.
    if (productSaveStatus && (
      productSaveStatus.status === 'completed'
      || productSaveStatus.status === 'failed'
      || productSaveStatus.status === 'cancelled'
    )) return;

    let cancelled = false;
    const tick = async () => {
      try {
        const s = await getProductSaveJobStatus(productSaveJobId);
        if (cancelled) return;
        setProductSaveStatus({
          status: s.status,
          processed: s.processedCount,
          target: s.targetCount,
        });
        // If we previously surfaced the "Add at least one product" error
        // from /api/brand-setup/start (race: user clicked Generate before
        // any rows landed), clear it once at least one product has been
        // written. The button gating below means this shouldn't be
        // reachable going forward, but it lets users coming from a
        // pre-fix state self-recover without a hard refresh.
        if (s.processedCount > 0) {
          setStepError(prev => prev.review ? { ...prev, review: undefined } : prev);
        }
      } catch (err) {
        // Single transient failure shouldn't abort the poll. The next
        // tick retries; if the job is genuinely dead, completion of
        // /api/brand-setup/start will reveal it via its own error.
        console.warn('[product-save poll]', err instanceof Error ? err.message : err);
      }
    };
    void tick();
    // Jun 15 — bumped 3000ms → 800ms so the counter visibly streams. The
    // backend writes progress on EVERY save completion (no batching), so
    // a tighter poll exposes the true streaming pipeline. Was creating
    // an illusion of "batches of 30" because 20-parallel saves at ~0.4s
    // each landed ~40 saves per 3-second window.
    const id = window.setInterval(tick, 800);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [productSaveJobId, productSaveStatus?.status]);

  // Jun 15 — auto-advance from save-products → find-competitors when
  // the save job completes. Sits on the same screen showing the loader
  // until status flips. Failure stays on the step + surfaces the error
  // so the user can refresh + retry.
  //
  // CRITICAL: also re-fetches the saved products from the backend so
  // their `id` field becomes the REAL DB UUID (not the source-url
  // string used by the discovery payload). Without this swap, the
  // featured-product picker modal would pass source-url strings as
  // `featured_product_ids` to /api/brand-setup/start; the backend
  // would cast `ANY($2::uuid[])` over those strings and either error
  // or match zero rows → "Add at least one product" 400. Same applies
  // to selectedProducts: rebuild from sourceUrl matching.
  useEffect(() => {
    if (step !== 'save-products') return;
    if (productSaveStatus?.status !== 'completed') return;
    let cancelled = false;
    (async () => {
      try {
        // listProducts returns the SAVED rows with real DB UUIDs +
        // image URLs hosted on GCS (vs the remote storefront CDN URLs
        // used during discovery).
        const saved = await logTiming(
          'listProducts (poll for save progress)',
          () => listProducts(),
        );
        if (cancelled) return;
        // Remap selectedProducts (which currently holds source_url
        // strings from discovery) to the real DB UUIDs of the saved
        // rows. Matched by sourceUrl. Any rows that didn't save (rare
        // failure path) just drop out of the selection.
        const sourceUrlToId = new Map<string, string>();
        for (const r of saved) {
          if (r.sourceUrl) sourceUrlToId.set(r.sourceUrl, r.id);
        }
        const nextSelected = new Set<string>();
        for (const oldId of selectedProducts) {
          // oldId WAS the source_url at brand-step time
          const realId = sourceUrlToId.get(oldId);
          if (realId) nextSelected.add(realId);
        }
        setProducts(saved);
        setSelectedProducts(nextSelected);
        advance('find-competitors');
      } catch (err) {
        if (cancelled) return;
        console.warn(
          '[save-products] post-save listProducts failed:',
          err instanceof Error ? err.message : err,
        );
        // Even if the refetch fails, advance — the user can still
        // proceed; the picker modal will show whatever's in state.
        advance('find-competitors');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, productSaveStatus?.status]);

  useEffect(() => {
    if (step === 'gather' && !startedRef.current.has('gather')) {
      startedRef.current.add('gather'); void runGather();
    }
    // Product discovery fires the moment the brand step appears so the
    // grid populates while the user reviews/edits brand identity above.
    // Errors land in stepError['brand'] and are surfaced inside the
    // Products section (not at the top of the page) so brand edits aren't
    // visually blocked.
    // Skip auto-fire when the user came in via "Continue manually" — there's
    // no real URL to scrape products from, and we don't want a confusing
    // error in the Products card right after a manual fallback. The user
    // adds products by hand from the /products page later.
    if (step === 'brand' && !startedRef.current.has('brand-products') && !enteredManually) {
      startedRef.current.add('brand-products'); void runImport();
    }
    // Jun 15 — save-products fires saveProductsAsync once on entry,
    // then sits showing X/Y progress. The auto-advance effect below
    // watches productSaveStatus and bumps us to find-competitors when
    // status flips to 'completed'.
    if (step === 'save-products' && !startedRef.current.has('save-products')) {
      startedRef.current.add('save-products'); void runSaveProducts();
    }
    // Jun 15 — seed the featured picker with the first TARGET_COUNT
    // products by default (one per ad). User can deselect / pick
    // others on the pick-featured-products step. Only seed once.
    if (
      step === 'pick-featured-products'
      && featuredProductIds.length === 0
      && !startedRef.current.has('seed-featured')
    ) {
      startedRef.current.add('seed-featured');
      const pickedIds = products
        .filter(p => selectedProducts.has(p.id))
        .slice(0, MAX_FEATURED_PRODUCTS)
        .map(p => p.id);
      if (pickedIds.length > 0) {
        setFeaturedProductIds(pickedIds);
      }
    }
    // Loading steps must also handle the back-then-forward case. If the
    // user already produced a result on a prior visit, the result-state
    // (suggestions / opening / competitorAds) will still be populated;
    // auto-advance to the next step rather than re-running the
    // network call (and avoid the stale-startedRef bug where re-entering
    // this step would spin forever).
    if (step === 'find-competitors') {
      if (suggestions.length > 0) {
        advance('pick-competitors');
      } else if (!startedRef.current.has('find-competitors')) {
        startedRef.current.add('find-competitors'); void runFindCompetitors();
      }
    }
    if (step === 'competitor-ads') {
      // Re-entry: if we've already fetched ads for every currently-picked
      // competitor, skip the loading screen entirely. Otherwise re-run.
      const picked = suggestions.filter(s => selectedCompetitors.has(s.query));
      const allFetched = picked.length > 0 && picked.every(s => adsFetchedQueries.has(s.query));
      if (allFetched) {
        advance('find-opening');
      } else if (!startedRef.current.has('competitor-ads')) {
        startedRef.current.add('competitor-ads'); void runLoadAds();
      }
    }
    if (step === 'find-opening') {
      if (openingBrief) {
        advance('review');
      } else if (!startedRef.current.has('find-opening')) {
        startedRef.current.add('find-opening'); void runFindOpening();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const runGather = async () => {
    clearErr('gather');
    clearErr('url');
    try {
      const result = await logTiming(
        'scrapeBrand (URL → brand info)',
        () => scrapeBrand(websiteUrl.trim()),
      );
      // Stage the scrape result into the in-memory draft. No DB writes yet
      // — the user reviews + edits on the brand step, and we commit a
      // single createBrand → patchBrand → activateBrand chain in
      // handlePickContinue. This means cancellation / browser close /
      // scrape failure leaves zero rows behind and consumes no plan quota.
      setBrandDraft({
        name: result.name || '',
        brief: result.brief || '',
        colors: result.colors || [],
        logoBase64: result.logoBase64 ?? null,
        sourceUrl: result.sourceUrl || websiteUrl.trim(),
        faviconUrl: result.faviconUrl ?? null,
        images: result.images || [],
        typography: result.typography || [],
        fonts: result.fonts || [],
      });
      advance('brand');
    } catch (err) {
      // Bounce back to the URL screen with the error surfaced there. The
      // user can edit the URL + try again OR click "Continue manually" from
      // the URL step's error banner. Drop the 'gather' startedRef so a
      // second attempt actually fires (the useEffect guards on it).
      const msg = err instanceof Error
        ? err.message
        : 'Could not read this site. Try a different URL or continue manually.';
      setErr('url', msg);
      startedRef.current.delete('gather');
      setStep('url');
    }
  };

  // Used when /api/scrape-brand fails (most often a WAF block on enterprise
  // sites like heb.com / kroger.com — Akamai 403s anything off-residential).
  // Creates an empty brand seeded from the URL's hostname so the wizard can
  // continue; the user fills in name / colors / logo on the /brand page later.
  const handleManualEntry = () => {
    clearErr('gather');
    clearErr('url');
    setEnteredManually(true);
    const url = websiteUrl.trim();
    let host = '';
    try { host = new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, ''); }
    catch { host = ''; }
    // Seed the brand name from the hostname so the review screen isn't
    // completely blank, but keep sourceUrl null so we don't persist a
    // malformed URL when the user clicks Continue. No DB writes — same
    // deferred-commit invariant as runGather.
    const seedName = host ? host.split('.')[0].replace(/^./, c => c.toUpperCase()) : '';
    setBrandDraft({
      name: seedName,
      brief: '',
      colors: [],
      logoBase64: null,
      sourceUrl: null,
      faviconUrl: null,
      images: [],
      typography: [],
      fonts: [],
    });
    advance('brand');
  };

  // Discovers products from a URL. Defaults to the wizard's brand URL but
  // accepts an override — that's how the "Add Products" affordance in the
  // brand step's empty Products card lets manual-entry users import from a
  // different storefront URL than their brand identity URL.
  const runImport = async (sourceOverride?: string) => {
    const source = (sourceOverride ?? websiteUrl).trim();
    if (!source) {
      setErr('brand', 'Paste a URL to import products from.');
      return;
    }
    clearErr('brand');
    setProductsLoading(true);
    try {
      // FAST discovery only — returns the candidate list (remote images) in
      // ~1-2s for Shopify brands. Slow per-product I/O (download / classify /
      // upload) is deferred to handlePickContinue when the user clicks Save.
      const discovered = await logTiming(
        'discoverProducts (Shopify catalog scrape)',
        // Discover up to the user's tier cap (Pro & above = full catalog).
        () => discoverProducts(source, productLimitForTier(planTier)),
      );
      setProducts(discovered);
      setSelectedProducts(new Set(discovered.map(p => p.id)));
      // If discovery succeeds from an override URL, treat the wizard as no
      // longer "manual" — the user has given us a real storefront. This
      // also unblocks the auto-fire guard if they hit Retry later.
      if (sourceOverride) {
        setEnteredManually(false);
        startedRef.current.add('brand-products');
      }
    } catch (err) {
      setErr('brand', err instanceof Error ? err.message : 'Could not find products.');
    } finally {
      setProductsLoading(false);
    }
  };

  // Used by the empty-state "Add Products" affordance. Same code path as the
  // auto-fire, but driven by a URL the user pastes inline.
  const handleImportFromUrl = (url: string) => {
    startedRef.current.add('brand-products');
    void runImport(url);
  };

  // The exact patch payload we backfill onto a freshly-created brand — kept in
  // one place so the lazy-on-upload path and the Continue path stay identical.
  const brandPatchPayload = () => ({
    brief: brandDraft?.brief || null,
    colors: brandDraft?.colors ?? [],
    fonts: brandDraft?.fonts,
    sourceUrl: brandDraft?.sourceUrl,
    faviconUrl: brandDraft?.faviconUrl,
    logoBase64: brandDraft?.logoBase64,
  });

  // create + patch + activate the brand (charges plan-quota on create). Idempotent:
  // returns the already-created brand when one exists (e.g. lazily created during
  // an image upload), re-patching it with the latest draft edits.
  const ensureBrandActive = async (): Promise<BrandRecord> => {
    if (createdBrandRef.current) {
      const patched = await patchBrand(createdBrandRef.current.id, brandPatchPayload());
      createdBrandRef.current = patched;
      setCreatedBrand(patched);
      return patched;
    }
    let brand = await createBrand((brandDraft?.name || '').trim() || 'New Brand');
    brand = await patchBrand(brand.id, brandPatchPayload());
    await activateBrand(brand.id);
    createdBrandRef.current = brand;
    setCreatedBrand(brand);
    return brand;
  };

  // Manual image upload — the WAF-blocked fallback (ae.com/KitKat) where neither
  // discovery nor per-URL scrape can reach products. Uploads persist IMMEDIATELY
  // (real user_products row + GCS), so we lazily create+activate the brand first
  // (the endpoint reads the active brand server-side). The saved product lands in
  // the grid with a real UUID — no candidate/remap dance — and is excluded from
  // the Continue-time bg save (already persisted). Name/description auto-fill via
  // vision on the backend.
  const acceptSavedProducts = (added: UserProduct[]) => {
    for (const p of added) savedUploadIdsRef.current.add(p.id);
    setProducts(prev => [...added, ...prev]);
    setSelectedProducts(prev => {
      const n = new Set(prev);
      added.forEach(p => n.add(p.id));
      return n;
    });
    clearErr('brand');
  };

  const handleUploadProductFile = async (file: File) => {
    await ensureBrandActive();
    try {
      const result = await uploadProduct(file);
      if (result.added.length === 0) throw new Error('Could not save the product.');
      acceptSavedProducts(result.added);
    } catch (err) {
      // Vision couldn't identify it → open the inline name+brief form instead
      // of saving. The product is NOT in the library until the user finishes.
      if (err instanceof ProductNeedsInfoError) {
        setManualProduct({
          file,
          previewUrl: URL.createObjectURL(file),
          name: err.suggestedName,
          description: err.suggestedDescription,
          saving: false,
          error: null,
        });
        return;
      }
      throw err;
    }
  };

  // Jul 12 — resubmit the pending manual product WITH the user's name + brief.
  // Backend skips vision (name provided) → text-classifies → saves.
  const submitManualProduct = async () => {
    if (!manualProduct) return;
    const name = manualProduct.name.trim();
    const description = manualProduct.description.trim();
    if (!name || !description) {
      setManualProduct(m => m && { ...m, error: 'Add both a name and a short description.' });
      return;
    }
    setManualProduct(m => m && { ...m, saving: true, error: null });
    try {
      const result = await uploadProduct(manualProduct.file, name, description);
      if (result.added.length === 0) throw new Error('Could not save the product.');
      acceptSavedProducts(result.added);
      URL.revokeObjectURL(manualProduct.previewUrl);
      setManualProduct(null);
    } catch (err) {
      setManualProduct(m => m && {
        ...m, saving: false,
        error: err instanceof Error ? err.message : 'Could not save the product.',
      });
    }
  };

  const cancelManualProduct = () => {
    if (manualProduct) URL.revokeObjectURL(manualProduct.previewUrl);
    setManualProduct(null);
  };

  // Jul 12 — user-uploaded BRAND PHOTO (the honest fallback when scraping
  // couldn't pull imagery — WAF CDNs / image-less sites). Same lazy-brand
  // pattern as the product upload: persist to GCS + brands.imagery, then
  // append the GCS URL to the draft so it displays AND feeds DNA + the
  // no-product generation (brandDraft.images is what gets passed along).
  const handleUploadBrandImage = async (file: File) => {
    await ensureBrandActive();
    const result = await uploadBrandImage(file);
    const urls = result.images.map((r) => r.url).filter(Boolean);
    setBrandDraft((prev) => prev
      ? { ...prev, images: Array.from(new Set([...prev.images, ...urls])) }
      : prev);
  };

  // Single commit point for the wizard. Runs only after the user has
  // reviewed the brand identity AND clicked Continue. Sequence:
  //   1. ensureBrandActive — create+patch+activate (or reuse a brand lazily
  //      created during an image upload); charges plan-quota on first create
  //   2. saveProductsAsync — fire-and-forget bg save of DISCOVERED picks only
  //      (image-uploaded products are already persisted)
  // If ensureBrandActive fails, nothing downstream runs — the error stays on
  // the brand step + the user can retry without burning quota.
  const handlePickContinue = async () => {
    if (!brandDraft) return;
    const picked = products.filter(p => selectedProducts.has(p.id));
    setSavingProducts(true);
    clearErr('brand');
    try {
      await ensureBrandActive();
      // Jun 17 — defer product save. The save-products wizard step is
      // skipped; bg save runs fire-and-forget for ALL picked products.
      // The user lands on /find-competitors immediately (no 30-60s wait
      // for 357 products to download+classify+upload). At
      // pick-featured-products Continue we'll fast-save just the 5
      // picks if the bg job hasn't reached them yet (almost always it
      // has — find-competitors + competitor-ads take 25-50s, longer
      // than bg save needs for ~95% of a typical catalog).
      // Uploaded products are already saved → exclude from the bg save.
      const toSave = picked.filter(p => !savedUploadIdsRef.current.has(p.id));
      if (toSave.length > 0) {
        void logTiming(
          `saveProductsAsync (background, ${toSave.length} products)`,
          () => saveProductsAsync(toSave),
        )
          .then((result) => {
            setProductSaveJobId(result.jobId);
            setProductSaveStatus({
              status: 'running',
              processed: 0,
              target: result.targetCount,
            });
          })
          .catch((err) => {
            console.warn('[wizard] background save kickoff failed:', err);
          });
      }
      // Jul 11 — brand imagery + Brand DNA now run in the BACKGROUND here (no
      // dedicated screen). The brand is created + active with its source_url,
      // so scrape the homepage for campaign/lifestyle shots → distill Brand DNA
      // → it's ready by the time the 20-gen fires (competitors + concepts take
      // 30-60s). DNA is the ONLY brand-style signal on the freestyle (no-recipe)
      // render path, so keep building it even though we no longer show it. The
      // whole thing is best-effort — a failure just means no DNA nudge.
      // NOTE: screen 1 (the scraper) is untouched — this runs after Continue,
      // on the way to competitors, so onboarding speed is unchanged.
      if (brandDraft.sourceUrl) {
        void (async () => {
          try {
            await scrapeBrandImagery({ limit: 10, imageUrls: brandDraft.images });
            await buildBrandDna();
          } catch (e) {
            console.warn('[wizard] background brand-imagery/DNA build failed:', e);
          }
        })();
      }
      advance('competitors-intro');
    } catch (err) {
      setErr('brand', err instanceof Error ? err.message : 'Could not save brand.');
    } finally {
      setSavingProducts(false);
    }
  };

  // Jul 11 — NO-PRODUCT path. When the user continues with zero products we
  // don't dead-end them: after an encouragement prompt, create the brand and
  // generate on-brand LIFESTYLE ads from the brand's own site imagery (the
  // /start-brand-imagery worker scrapes the reference photos itself). Skips the
  // whole product-centric competitors/concepts phase → straight to My Creatives.
  // PATH1-DORMANT (Jul 12) — no longer reachable (product is mandatory). Kept
  // for now; delete with the backend no-product path at cleanup.
  const handleContinueNoProducts = async () => {
    const ok = await confirm({
      title: 'Continue without products?',
      message:
        "We strongly recommend adding at least one product for the best creatives. " +
        "With no product selected, the app will generate brand-imagery ads from " +
        "your site's photos instead.",
      confirmLabel: 'Generate brand imagery',
      cancelLabel: 'Add a product',
    });
    if (!ok || !brandDraft) return;
    setSavingProducts(true);
    clearErr('brand');
    try {
      const brand = await ensureBrandActive();
      // Best-effort background imagery + DNA build (helps future runs / unlocks).
      if (brandDraft.sourceUrl) {
        void (async () => {
          try { await scrapeBrandImagery({ limit: 10, imageUrls: brandDraft.images }); await buildBrandDna(); }
          catch { /* non-fatal */ }
        })();
      }
      // Pass the brand imagery we already found on the review screen so the
      // worker renders from the SAME photos the user saw (no divergent re-scrape).
      const { jobId, targetCount, totalCount } = await startBrandImagery({
        aspectRatio: '1:1',
        imageUrls: brandDraft.images,
      });
      // Same gallery handoff as the product Generate — sessionStorage scoped by
      // brand so /gallery draws placeholder tiles immediately on mount.
      try {
        sessionStorage.setItem(`brand_setup_active_job:${brand.id}`, JSON.stringify({
          jobId, targetCount, totalCount, startedAt: Date.now(), brandId: brand.id,
        }));
      } catch { /* private mode etc. */ }
      window.location.assign('/gallery');
    } catch (err) {
      setErr('brand', err instanceof Error ? err.message : 'Could not start generation.');
      setSavingProducts(false);
    }
  };

  // Jun 15 — runs when the user enters the save-products step. Fires the
  // async save-batch endpoint (returns a job_id instantly + spawns a
  // background worker) and seeds productSaveStatus so the polling
  // effect picks it up. The step's render uses WizardLoadingStep with
  // a live "X / Y" subtitle; auto-advance to find-competitors when the
  // job's status flips to 'completed' (see auto-advance effect below).
  const runSaveProducts = async () => {
    clearErr('save-products');
    // Exclude image-uploaded products — they're already persisted.
    const picked = products.filter(
      p => selectedProducts.has(p.id) && !savedUploadIdsRef.current.has(p.id),
    );
    if (picked.length === 0) {
      // Defensive — handlePickContinue should have routed us straight to
      // find-competitors instead. Bail without spawning a no-op job.
      advance('find-competitors');
      return;
    }
    try {
      const result = await logTiming(
        `saveProductsAsync (kick off bg save of ${picked.length} products)`,
        () => saveProductsAsync(picked),
      );
      setProductSaveJobId(result.jobId);
      setProductSaveStatus({
        status: 'running',
        processed: 0,
        target: result.targetCount,
      });
    } catch (err) {
      setErr('save-products', err instanceof Error ? err.message : 'Could not save products.');
    }
  };

  // Jul 23 — save a suggested competitor to the My Competitors library using
  // the SAME reliable path as the /competitors Suggest tool. When the suggest
  // endpoint resolved the exact Meta advertiser page (page_id present), add
  // page-scoped so every future pull returns ALL of that advertiser's ads
  // (not the 1-2 that keyword-match the name) AND we capture their FB profile
  // logo. Falls back to the query-based add only when no page_id resolved.
  const saveSuggestedCompetitor = (s: CompetitorSuggestion, label: string) =>
    (s.page_id
      ? addCompetitorPage({ pageId: s.page_id, name: label || s.name })
      : addCompetitor({ query: s.query, label })
    ).catch((err) => {
      console.warn(
        `[onboarding] background library save failed for ${s.query}:`,
        err instanceof Error ? err.message : err,
      );
    });

  const runFindCompetitors = async () => {
    clearErr('find-competitors');
    competitorsSkippedRef.current = false;
    // Fresh controller per run — the previous one may have been aborted.
    const ctrl = new AbortController();
    competitorsAbortRef.current = ctrl;
    try {
      const r = await logTiming(
        'suggestCompetitors (LLM seed 10 candidates)',
        () => suggestCompetitors(ctrl.signal),
      );
      if (competitorsSkippedRef.current) return;
      // Jul 23 — keep ONLY competitors that actually run active Meta ads, so
      // onboarding surfaces exactly what the /competitors Suggest tool does.
      // The suggest endpoint resolves each LLM seed to its exact advertiser
      // page via search_pages (which only returns advertisers currently
      // running ads) and carries active_ad_count. Drop any zero-count page so
      // we never suggest a competitor with nothing to analyze. (Missing count
      // → treat as active so a provider hiccup never hides everything.)
      const active = r.suggestions || [];
      // Jul 24 — suggest is LLM-only now (no per-candidate Apify validation),
      // so show a fuller list (up to 9) and PRE-SELECT only the FIRST 4 (the
      // picker caps at 4). Continue then scrapes ads for just those ≤4 picks —
      // NOT the whole list — which is the entire point of the cost rewrite
      // (was: auto-select-all → scrape all 6, ~$2 of Apify per run).
      const list = active.slice(0, 9);
      setSuggestions(list);
      // Jul 24 — pre-select NONE. The user actively picks up to 4 (the picker
      // caps at 4); Continue only scrapes the ones they choose. (Was: auto-
      // select all/first-4 → scraped picks the user never chose.)
      setSelectedCompetitors(new Set());
      advance('pick-competitors');
    } catch (err) {
      // AbortError = intentional Skip; swallow silently.
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (competitorsSkippedRef.current) return;
      setErr('find-competitors', err instanceof Error ? err.message : 'Could not suggest competitors.');
    } finally {
      if (competitorsAbortRef.current === ctrl) {
        competitorsAbortRef.current = null;
      }
    }
  };

  const runLoadAds = async () => {
    clearErr('competitor-ads');
    adsSkippedRef.current = false;
    const ctrl = new AbortController();
    adsAbortRef.current = ctrl;
    // Dedupe by query — the LLM occasionally emits two suggestions with
    // the SAME facebook_handle (different display names but same handle).
    // Without dedup we'd hit Apify twice for the same page + render the
    // section twice + the row count would feel "off" (3 selected → 4
    // sections shown). The Set preserves first-seen order.
    const seen = new Set<string>();
    const picked = suggestions
      .filter(s => selectedCompetitors.has(s.query))
      .filter(s => {
        if (seen.has(s.query)) return false;
        seen.add(s.query);
        return true;
      });
    // Diagnostic — verify the picked list matches what the user selected
    // on the previous screen. (Issue 3.)
    console.log(
      '[runLoadAds] picked', picked.length, 'of', selectedCompetitors.size,
      'selected:', picked.map(p => p.query),
    );
    // Reset state for the new run + show the unified WizardLoadingStep
    // (instead of streaming pending placeholders into AdsStep as ads
    // arrive — feedback was the mixed pending/loaded UI felt noisy).
    setCompetitorAds([]);
    setAdsFetchedQueries(new Set());
    setAdsLoading(true);

    // Read-only ad fetching — fetchCompetitorAdsByQuery hits the shared
    // _gather_advertiser (cache + in-flight dedup + Apify) WITHOUT
    // writing to user_competitors. Wall time = slowest Apify call (NO
    // frontend timeout — cold cache hits need the full Apify latency).
    const results = await Promise.allSettled(
      picked.map(async (s) => {
        const r = await logTiming(
          `fetchCompetitorAdsByQuery(${s.query})`,
          () => fetchCompetitorAdsByQuery(s.query, ctrl.signal),
        );
        // Pool of 12 (backend returns up to AD_TOP_N=15) so that after we drop
        // image-less ads below, a full dense 6-up row still fills.
        const raw = (r.ads || []).slice(0, 12);
        // Drop ads with no image — both media_cached_url and media_ref
        // falsy means Apify scraped the ad metadata but the creative
        // was text-only or the image link was already expired. These
        // render as gray "No image" tiles which dilute the grid.
        const ads = raw.filter(a => a.media_cached_url || a.media_ref);
        // Trust the SCRAPED advertiser name from the first ad over the
        // LLM-emitted name. The suggest-competitors LLM sometimes
        // hallucinates handles (e.g. emits {name: "Glossier",
        // facebook_handle: "glossier-skincare-shop"} where that handle
        // is actually a random reseller). Using ads[0].advertiser shows
        // what's actually being scraped. Falls back to the LLM name
        // when no ads in this bundle have advertiser metadata.
        const actualAdvertiser = ads.find(a => a.advertiser)?.advertiser
          || raw.find(a => a.advertiser)?.advertiser;
        const label = actualAdvertiser || s.name;
        if (actualAdvertiser && actualAdvertiser.toLowerCase() !== s.name.toLowerCase()) {
          console.warn(
            `[runLoadAds] LLM mislabel: claimed "${s.name}" `
            + `but Apify returned ads for "${actualAdvertiser}" `
            + `(handle ${s.query})`,
          );
        }
        // Save EVERY selected competitor to the My Competitors library in the
        // background (Jul 12) — page-scoped when the advertiser page resolved
        // (Jul 23), matching the /competitors Suggest tool. The backend SAVES
        // handle-less competitors (0 ads) instead of 404ing, so this no longer
        // spams the console AND the promise we make the user holds: the full
        // competitor list is tracked in /competitors.
        void saveSuggestedCompetitor(s, label);
        return { query: s.query, label, ads };
      })
    );

    // Build the final bundle list — KEEP EVERY selected competitor (Jul 12).
    // Previously we dropped competitors with 0 image-bearing ads, which
    // silently hid the ones we couldn't pull (WAF-blocked sites) — the user
    // saw fewer competitors than they picked and had no idea why. Now each
    // competitor always appears; AdsStep shows an honest "no live ads" state
    // for the empty ones. Order follows the picked list.
    const bundles: CompetitorAdsBundle[] = results.map((res, i) =>
      res.status === 'fulfilled'
        ? res.value
        : { query: picked[i].query, label: picked[i].name, ads: [] },
    );
    // Jun 15 — Skip cleanup. ctrl.abort() may have fired by now and
    // some of the picked.map promises rejected with AbortError; the
    // `Promise.allSettled` doesn't surface them, so we just see fewer
    // bundles. Either way, don't paint stale state.
    if (adsSkippedRef.current) {
      setAdsLoading(false);
      if (adsAbortRef.current === ctrl) adsAbortRef.current = null;
      return;
    }
    // Jul 24 — DISPLAY only the competitors that actually returned live ads.
    // The 0-ad picks (WAF-blocked / not currently advertising) are still
    // saved to My Competitors above via saveSuggestedCompetitor, so nothing
    // is lost — the user manages them there. This screen stays focused on
    // "here are the competitors who are running ads."
    setCompetitorAds(bundles.filter(b => Array.isArray(b.ads) && b.ads.length > 0));
    setAdsFetchedQueries(new Set(picked.map(p => p.query)));
    setAdsLoading(false);
    if (adsAbortRef.current === ctrl) adsAbortRef.current = null;
  };

  const runFindOpening = async () => {
    clearErr('find-opening');
    clearErr('review');
    if (!createdBrand) {
      setErr('find-opening', 'Brand context missing.');
      return;
    }
    try {
      // Jun 15 — call /write-concepts (product-aware) instead of
      // /preview (brand-generic). featuredProductIds was set by the
      // pick-featured-products step that fires immediately before this
      // loader. competitor_blocks omitted = brand+product-only prompt
      // branch on the backend (matches the wizard skip behavior).
      const result = await logTiming(
        `writeBrandSetupConcepts (LLM brief + 20 concepts for ${featuredProductIds.length} featured products)`,
        () => writeBrandSetupConcepts({
          featuredProductIds: featuredProductIds,
          // Jul 23 — pass the competitor ads the user pulled in the
          // competitor-ads step so the brief is competitor-aware (was
          // dropped → brief always said "no competitor ads found").
          // Only bundles that actually returned ads; empty ones (skip
          // path / no-ads competitors) fall through to the backend's
          // brand+product-only branch.
          competitorBlocks: competitorAds.filter(
            (b) => Array.isArray(b.ads) && b.ads.length > 0,
          ),
        }),
      );
      setOpeningBrief(
        result.opening || 'Stand apart with a distinct creative direction.',
      );
      setWinningIdeas(result.winning_ideas ?? []);
      setCachedPlan(result.plan ?? null);
      setThemes([]);  // unused on review now; cleared so stale UI doesn't paint
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Brand-setup preview failed.';
      if (err instanceof InsufficientCreditsError || /Need \d+ credits/i.test(msg)) {
        setErr('review', msg);
      }
      setOpeningBrief('Stand apart with a distinct creative direction.');
      setWinningIdeas([]);
      setThemes([]);
      setCachedPlan(null);
      console.error('[wizard] /write-concepts failed:', err);
    } finally {
      advance('review');
    }
  };

  const handleStartFromUrl = () => {
    clearErr('url');
    const invalid = validateWebsiteUrl(websiteUrl);
    if (invalid) { setErr('url', invalid); return; }
    // Resubmitting after a previous failure — clear the manual flag so the
    // brand step re-fires product discovery if this attempt succeeds.
    setEnteredManually(false);
    advance('gather');
  };

  const handleGenerate = async () => {
    clearErr('review');
    setGenerating(true);
    // The activation click — the moment onboarding pays off. Pairs with
    // `brand_setup_started` below to separate "clicked" from "batch actually
    // fired" (e.g. a 402/credit gate would log the click but not the start).
    track('brand_setup_generate_clicked', { target: TARGET_COUNT });
    try {
      // Pass cachedPlan from /preview so the backend worker + stub
      // inserter skip per-slot LLM concept calls. Null = legacy fallback
      // (worker rebuilds concepts at render-time).
      const { jobId, totalCount } = await logTiming(
        'startBrandSetup (Generate button → /start endpoint)',
        () => startBrandSetup(
          TARGET_COUNT,
          cachedPlan ?? undefined,
          // Jun 15 — when the user sub-selected products on the review
          // step, pass those ids so the backend rebuilds the plan from
          // JUST those products. Empty = legacy "use all" behavior.
          featuredProductIds.length > 0 ? featuredProductIds : undefined,
        ),
      );
      // Batch actually kicked off (job id returned) — the real activation.
      track('brand_setup_started', { total: totalCount, target: TARGET_COUNT });
      // Hand the job id + totalCount to /gallery so it can render ALL
      // placeholder tiles (target rendering + total-target locked dead
      // boxes) on first paint, before any DB rows land. sessionStorage
      // survives the redirect. Scoped by brand_id so switching to a
      // different brand on /gallery doesn't bleed skeletons across.
      try {
        const brandId = createdBrand?.id || 'unknown';
        sessionStorage.setItem(`brand_setup_active_job:${brandId}`, JSON.stringify({
          jobId,
          targetCount: TARGET_COUNT,
          totalCount,
          startedAt: Date.now(),
          brandId,
        }));
      } catch { /* private mode etc. */ }
      window.location.assign('/gallery');
    } catch (err) {
      setErr('review', err instanceof Error ? err.message : 'Could not start generation.');
      setGenerating(false);
    }
  };

  const canAfford = (balance ?? 0) >= TARGET_COUNT;
  const segmentIdx = getProgressSegmentIndex(step);

  // Cancel exits the wizard back to the brand list — wired into the
  // header's top-right "✕ Cancel" link below. No confirm dialog
  // (explicit click = deliberate intent).

  return (
    <div className="h-screen w-full bg-surface flex flex-col relative overflow-hidden">
      <MeshBg />
      <header className="relative z-10 flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-surface-dark/30 bg-white">
        <a href="/" className="flex items-center">
          <Logo height={56} />
        </a>
        <button
          onClick={() => navigate('/brands')}
          className="text-sm font-bold text-dark-muted hover:text-dark transition-colors cursor-pointer flex items-center gap-1.5"
        >
          <i className="ri-close-line" />
          Cancel
        </button>
      </header>

      <main className="relative z-10 flex-1 min-h-0 flex flex-col items-center px-3 sm:px-6 py-4 sm:py-8">
        <div className="w-full max-w-[1240px] flex-1 min-h-0 flex flex-col">
          <ProgressBar segmentIdx={segmentIdx} />

          {/* Bounded to the viewport so the footer (Continue/Skip) is ALWAYS
              visible even at ~800px height: the card is a flex column capped
              at the viewport, the step body scrolls internally, and the footer
              is pinned below it (flex-shrink-0). */}
          <div className="gradient-stroke bg-white rounded-2xl shadow-2xl shadow-primary/45 p-4 sm:p-6 mt-4 flex flex-col flex-1 min-h-0 max-h-[700px]">
            {/* Inner key={step} replays the entry animation on each step
                change. Top-level step errors render above the step body
                (except brand, which has its own per-section error UX). */}
            <div
              key={step}
              className="flex-1 min-h-0 overflow-y-auto"
              style={{ animation: 'stepEnter 400ms cubic-bezier(0.16,1,0.3,1) both' }}
            >
              {stepError[step] && step !== 'brand' && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex items-start gap-3">
                  <span className="flex-1">{stepError[step]}</span>
                  {step === 'url' && websiteUrl.trim() && (
                    <button
                      type="button"
                      onClick={handleManualEntry}
                      className="flex-shrink-0 px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary-hover font-semibold text-xs"
                    >
                      Continue manually →
                    </button>
                  )}
                  {/* Jun 15 — when the review step's error is the
                      "Add at least one product" 400 from the backend,
                      give the user a one-click hop back to the brand
                      step where the product picker lives. Caught
                      previously as a hard error with no escape. */}
                  {step === 'review'
                    && /add at least one product/i.test(stepError['review'] ?? '') && (
                    <button
                      type="button"
                      onClick={() => {
                        clearErr('review');
                        clearErr('brand');
                        advance('brand');
                      }}
                      className="flex-shrink-0 px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary-hover font-semibold text-xs"
                    >
                      Back to add products →
                    </button>
                  )}
                </div>
              )}

              {step === 'url' && (
                <UrlStep
                  websiteUrl={websiteUrl}
                  setWebsiteUrl={(v) => {
                    setWebsiteUrl(v);
                    // Clear any stale error as soon as the user edits — they're
                    // actively trying to fix the issue.
                    if (stepError['url']) clearErr('url');
                  }}
                  onSubmit={handleStartFromUrl}
                />
              )}
              {step === 'gather' && (
                <WizardLoadingStep
                  title="Reading your brand…"
                  subtitle="Pulling your logo, colors, and brief from the page"
                  expectedDurationMs={20000}
                />
              )}
              {step === 'brand' && brandDraft && (
                <BrandReviewStep
                  draft={brandDraft}
                  onDraftChange={setBrandDraft}
                  products={products}
                  productsLoading={productsLoading}
                  productsError={stepError['brand'] || null}
                  onImportFromUrl={handleImportFromUrl}
                  onUploadFile={handleUploadProductFile}
                  onUploadBrandImage={handleUploadBrandImage}
                  manualProduct={manualProduct}
                  onManualChange={(patch) => setManualProduct(m => m && { ...m, ...patch })}
                  onManualSubmit={submitManualProduct}
                  onManualCancel={cancelManualProduct}
                  selectedProducts={selectedProducts}
                  onToggleProduct={(pid) => setSelectedProducts(prev => {
                    const n = new Set(prev);
                    if (n.has(pid)) n.delete(pid); else n.add(pid);
                    return n;
                  })}
                  productSearch={productSearch}
                  onProductSearch={setProductSearch}
                />
              )}
              {step === 'save-products' && (
                <WizardLoadingStep
                  title="Saving your products…"
                  subtitle={
                    productSaveStatus
                      ? `${productSaveStatus.processed} of ${productSaveStatus.target} saved`
                      : 'Uploading images and writing product metadata'
                  }
                  // Jun 15 — drive the ring + percentage off real save
                  // counts so the visible number and the subtitle stay
                  // in sync. (Previously the ring used wall-time
                  // exponential approach and drifted out of step with
                  // "199 of 359 saved".)
                  progressPct={
                    productSaveStatus && productSaveStatus.target > 0
                      ? (productSaveStatus.processed / productSaveStatus.target) * 100
                      : 0
                  }
                />
              )}
              {step === 'competitors-intro' && (
                <CompetitorsIntroStep
                  onContinue={() => advance('find-competitors')}
                  onSkip={() => {
                    // Skip competitor analysis entirely — no LLM/Apify
                    // call has fired yet (find-competitors only runs once
                    // we LAND on it), so there's nothing to abort. Mark
                    // skipped so the find-opening loader uses the
                    // product-only subtitle script, then jump to the
                    // featured-product picker (must run before
                    // /write-concepts so concepts stay product-aware).
                    competitorsSkippedRef.current = true;
                    adsSkippedRef.current = true;
                    clearErr('find-competitors');
                    clearErr('competitor-ads');
                    void advanceWithGuard('pick-featured-products');
                  }}
                />
              )}
              {step === 'find-competitors' && (
                <WizardLoadingStep
                  title="Finding competitors…"
                  subtitle="Picking brands like yours from your brand profile"
                  expectedDurationMs={10000}
                />
              )}
              {step === 'pick-competitors' && (
                <CompetitorsStep
                  suggestions={suggestions}
                  selected={selectedCompetitors}
                  onToggle={(q) => setSelectedCompetitors(prev => {
                    const n = new Set(prev);
                    // Cap at 4 — track 4 deep competitors, not 10 shallow ones.
                    if (n.has(q)) n.delete(q);
                    else if (n.size < 4) n.add(q);
                    return n;
                  })}
                />
              )}
              {step === 'ads-intro' && (
                <CompetitorAdsIntroStep
                  onContinue={() => void advanceWithGuard('competitor-ads')}
                  onSkip={() => {
                    // Jul 17 — Skip the ad pull, but the competitors the
                    // user already picked on pick-competitors must still
                    // land in the My Competitors library (the ad-pull path
                    // saves them as a side-effect of runLoadAds; on Skip we
                    // save directly). Dedup by query to match runLoadAds.
                    const seen = new Set<string>();
                    const picked = suggestions
                      .filter(s => selectedCompetitors.has(s.query))
                      .filter(s => {
                        if (seen.has(s.query)) return false;
                        seen.add(s.query);
                        return true;
                      });
                    for (const s of picked) {
                      void saveSuggestedCompetitor(s, s.name);
                    }
                    adsSkippedRef.current = true;
                    clearErr('competitor-ads');
                    void advanceWithGuard('pick-featured-products');
                  }}
                />
              )}
              {step === 'competitor-ads' && (
                adsLoading ? (
                  <WizardLoadingStep
                    title="Pulling competitor ads…"
                    subtitle="Fetching the live Meta ads for the competitors you picked — this can take a minute."
                    expectedDurationMs={90000}
                  />
                ) : (
                  <AdsStep bundles={competitorAds} />
                )
              )}
              {step === 'pick-featured-products' && (
                <PickFeaturedProductsStep
                  products={products.filter(p => selectedProducts.has(p.id))}
                  selectedIds={featuredProductIds}
                  onChange={setFeaturedProductIds}
                />
              )}
              {step === 'find-opening' && (
                <WizardLoadingStep
                  title="Finding your opening…"
                  // Jun 15 — when the user Skipped competitor analysis,
                  // the "Comparing competitors / Spotting their gap"
                  // copy is a lie. Branch the subtitle script on
                  // whether we actually have any competitor signal.
                  subtitles={
                    competitorsSkippedRef.current || adsSkippedRef.current
                      || suggestions.length === 0
                      ? [
                          'Reading your brand voice…',
                          'Studying your product mix…',
                          'Distilling your brand promise…',
                          'Writing your 5 winning concepts…',
                          'Detailing all 20 ad directions…',
                          'Polishing the lineup…',
                        ]
                      : [
                          'Reading your brand voice…',
                          'Comparing what your competitors are running…',
                          'Spotting the gap they’re missing…',
                          'Writing your 5 winning concepts…',
                          'Detailing all 20 ad directions…',
                          'Polishing the lineup…',
                        ]
                  }
                  expectedDurationMs={90000}
                />
              )}
              {step === 'review' && (
                <WinningIdeasStep
                  opening={openingBrief}
                  winningIdeas={winningIdeas}
                />
              )}
            </div>
          </div>

          {/* Footer button row sits OUTSIDE the animated inner container so
              the buttons don't replay the entry animation on each step.
              Bottom Cancel was dropped Jun 15 — the header's top-right
              "✕ Cancel" link is the single exit. During find-competitors
              + competitor-ads loading, the CTA renders as "Skip"; on
              competitor-ads completion, it flips to "Continue". */}
          <div className="mt-5 pt-4 border-t border-surface-dark/15 flex-shrink-0 flex items-center justify-center gap-4">
            <StepFooterCta
              step={step}
              generating={generating}
              canAfford={canAfford}
              balance={balance ?? 0}
              savingProducts={savingProducts}
              selectedProducts={selectedProducts}
              selectedCompetitors={selectedCompetitors}
              adsLoading={adsLoading}
              hasPickedFeatured={featuredProductIds.length > 0}
              resolvingFeatured={resolvingFeatured}
              hasProductsAvailable={
                products.filter(p => selectedProducts.has(p.id)).length > 0
              }
              onBackToBrand={() => {
                // Jun 15 — wipe any stale brand error so the back-nav
                // doesn't paint the review-step "Add a product" banner
                // onto the brand step. Clear the review error too so
                // it doesn't reappear when the user returns.
                clearErr('review');
                clearErr('brand');
                advance('brand');
              }}
              onStart={handleStartFromUrl}
              onAdvance={advanceWithGuard}
              onGenerate={handleGenerate}
              onSaveProducts={handlePickContinue}
              onContinueNoProducts={handleContinueNoProducts}
              onSkipCompetitors={() => {
                // Jun 15 — abandon BOTH find-competitors and competitor-ads
                // in one step. The in-flight LLM/Apify call will still
                // resolve on the network layer (no AbortController yet),
                // but the ref check inside each runner discards the
                // result before touching state. Jump straight to the
                // winning-ideas loader.
                //
                // BUT: if we're skipping on competitor-ads, the user has
                // already picked their competitors on pick-competitors.
                // Those picks need to land in the My Competitors library
                // regardless of whether we waited for Apify to return ad
                // creatives. runLoadAds DOES fire addCompetitor as a
                // background side-effect, but only AFTER each Apify
                // promise resolves — Skip means we're not waiting for
                // that. Fire the saves directly here so the library is
                // populated whether Apify ever returns or not. Dedupe by
                // query (matches the runLoadAds dedup) so a competitor
                // emitted twice doesn't double-save.
                if (step === 'competitor-ads') {
                  const seen = new Set<string>();
                  const picked = suggestions
                    .filter(s => selectedCompetitors.has(s.query))
                    .filter(s => {
                      if (seen.has(s.query)) return false;
                      seen.add(s.query);
                      return true;
                    });
                  for (const s of picked) {
                    void saveSuggestedCompetitor(s, s.name);
                  }
                }
                competitorsSkippedRef.current = true;
                adsSkippedRef.current = true;
                // Jun 15 — actually cancel the in-flight HTTP requests
                // so the backend stops generating LLM tokens + stops
                // hitting external sites for FB-handle verification.
                // Without these aborts, /api/ad-strategist/suggest-
                // competitors ran to completion ($0.001 LLM + ~20
                // httpx connect-timeout retries per skip), wasting
                // money + tying up the backend worker.
                competitorsAbortRef.current?.abort();
                adsAbortRef.current?.abort();
                clearErr('find-competitors');
                clearErr('competitor-ads');
                setAdsLoading(false);
                // Jun 15 — Skip now lands at the product-picker step
                // (not find-opening). The picker must run BEFORE
                // /write-concepts so concepts are product-aware.
                advanceWithGuard('pick-featured-products');
              }}
            />
          </div>
        </div>
      </main>

      {/* Jun 15 — product picker modal. Lives at the wizard root so its
          backdrop covers everything including the footer. Bottom CTA on
          the review step opens it; "Select N products" confirms. */}
      <ProductPickerModal
        open={productPickerOpen}
        products={products.filter(p => selectedProducts.has(p.id))}
        initialSelected={featuredProductIds}
        onConfirm={(ids) => {
          setFeaturedProductIds(ids);
          setProductPickerOpen(false);
        }}
        onClose={() => setProductPickerOpen(false)}
      />

      {/* Keyframes scoped here so each step body replays the same entry
          animation. Kept inline (not in tailwind.config) so this file is
          self-contained. */}
      <style>{`
        @keyframes stepEnter {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// Jun 15 — find-competitors used to be a button-less loading step. It now
// renders a "Skip" button (handled in StepFooterCta below), so it's
// dropped from this list. gather + find-opening + save-products stay
// button-less because they can't be meaningfully skipped (save-products
// auto-advances on completion; the other two need their state to
// proceed).
function isLoadingStep(step: StepId): boolean {
  return step === 'gather' || step === 'find-opening' || step === 'save-products';
}

// ---------------------------------------------------------------------------
// Progress bar — 7 segments. Past = primary/60, current = primary,
// future = primary/20.
// ---------------------------------------------------------------------------

function ProgressBar({ segmentIdx }: { segmentIdx: number }) {
  return (
    <div className="flex items-center gap-2 px-2">
      {Array.from({ length: PROGRESS_TOTAL }, (_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full transition-colors ${
            i === segmentIdx
              ? 'bg-primary'
              : i < segmentIdx
                ? 'bg-primary/60'
                : 'bg-primary/20'
          }`}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CTA footer per step
// ---------------------------------------------------------------------------

interface StepFooterCtaProps {
  step: StepId;
  generating: boolean;
  canAfford: boolean;
  balance: number;
  savingProducts: boolean;
  selectedProducts: Set<string>;
  selectedCompetitors: Set<string>;
  /** Jun 15 — competitor-ads step: true while Apify is fetching ads.
   *  Flips the button label from Skip → Continue when ads arrive. */
  adsLoading: boolean;
  /** Jun 15 — pick-featured-products step: gates Continue until at
   *  least one product is selected. Also used on review step to
   *  decide whether to surface back-nav. */
  hasPickedFeatured: boolean;
  /** Jun 17 — true while the advance from pick-featured-products to
   *  find-opening is fast-saving any picks the bg job hasn't reached.
   *  Drives the Continue button's loading label. */
  resolvingFeatured: boolean;
  /** True when user picked products at brand step → reached the
   *  picker. False when they skipped → review needs to back-nav. */
  hasProductsAvailable: boolean;
  /** Jun 15 — jumps back to the brand step so the user can pick at
   *  least one product. Surfaced when they reached review with zero. */
  onBackToBrand: () => void;
  onStart: () => void;
  onAdvance: (s: StepId) => void;
  onGenerate: () => void;
  onSaveProducts: () => void;
  onContinueNoProducts: () => void;
  /** Jun 15 — bail out of BOTH competitor steps and jump straight to
   *  find-opening (winning ideas). Cancels in-flight LLM/Apify result
   *  via the runner-side refs. */
  onSkipCompetitors: () => void;
}

function StepFooterCta(p: StepFooterCtaProps) {
  if (isLoadingStep(p.step)) return null;

  if (p.step === 'url') {
    return <ContinueButton onClick={p.onStart}>Get started →</ContinueButton>;
  }
  if (p.step === 'brand') {
    // PRODUCT MANDATORY (Jul 12) — the app generates PRODUCT ads, so onboarding
    // can't complete without ≥1 product in the library. No "continue without
    // products" path. Blocked until the user adds one (store URL / upload /
    // library — all on this screen). Brand imagery stays optional (it feeds
    // Brand DNA), but is no longer a way to skip products.
    const n = p.selectedProducts.size;
    const blocked = n === 0;
    return (
      <div className="flex flex-col items-center gap-2">
        <ContinueButton
          disabled={p.savingProducts || blocked}
          onClick={p.onSaveProducts}
        >
          {p.savingProducts
            ? `Saving ${n} product${n === 1 ? '' : 's'}…`
            : blocked
              ? 'Add a product to continue'
              : `Save ${n} & continue →`}
        </ContinueButton>
        {blocked && (
          <p className="text-xs text-dark-muted/80 text-center max-w-md">
            Add at least one <span className="font-semibold">product</span> to
            continue — paste a store URL, upload a product photo, or pick from
            your library. Ads are built around your products.
          </p>
        )}
      </div>
    );
  }
  if (p.step === 'find-competitors') {
    // Jun 15 — Skip abandons competitor analysis entirely and jumps
    // straight to find-opening (winning ideas). The in-flight LLM call
    // is discarded on resolve via competitorsSkippedRef.
    return (
      <SkipButton onClick={p.onSkipCompetitors}>
        Skip →
      </SkipButton>
    );
  }
  if (p.step === 'pick-competitors') {
    // 4 is a MAXIMUM, not a minimum — the picker body caps selection at 4
    // (atCap). Continue needs ≥1 pick; requiring exactly 4 traps the user
    // when fewer than 4 competitors exist (e.g. only 3 found). At 0 picks
    // — including the "no competitors found" empty state — offer Skip so
    // the user is never stuck. Jul 17 — Continue advances to the
    // ads-intro gate (Screen 2), whose Continue fires the scrape.
    if (p.selectedCompetitors.size === 0) {
      return <SkipButton onClick={p.onSkipCompetitors}>Skip →</SkipButton>;
    }
    return (
      <ContinueButton onClick={() => p.onAdvance('ads-intro')}>
        Continue →
      </ContinueButton>
    );
  }
  if (p.step === 'competitor-ads') {
    if (p.adsLoading) {
      return (
        <SkipButton onClick={p.onSkipCompetitors}>
          Skip →
        </SkipButton>
      );
    }
    // Jun 15 — Continue now advances to pick-featured-products (not
    // find-opening). The picker runs BEFORE the concepts loader so
    // /write-concepts can be product-aware.
    return <ContinueButton onClick={() => p.onAdvance('pick-featured-products')}>Continue →</ContinueButton>;
  }
  if (p.step === 'pick-featured-products') {
    // Continue is gated on at least one product selected. The actual
    // selection happens inside the step body (see render branch).
    // Jun 17 — while advancing to find-opening, the wizard may need to
    // sync-save any picked products the bg job hasn't reached yet
    // (typically 0 — the bg save fires from handlePickContinue when
    // the user exits the brand step, and find-competitors +
    // competitor-ads take 25-50s of LLM/Apify time, more than enough
    // for the bg save of a typical catalog to finish). When it DOES
    // need to fast-save, show a loading label so the click doesn't
    // sit silent.
    return (
      <ContinueButton
        disabled={!p.hasPickedFeatured || p.resolvingFeatured}
        onClick={() => p.onAdvance('find-opening')}
      >
        {p.resolvingFeatured ? (
          <>
            <i className="ri-loader-4-line animate-spin mr-2" />
            Importing products…
          </>
        ) : (
          <>Continue →</>
        )}
      </ContinueButton>
    );
  }
  if (p.step === 'review') {
    if (!p.canAfford) {
      return (
        <a
          href="/checkout"
          className="px-8 py-3.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold transition-all cursor-pointer flex items-center gap-2 shadow-lg shadow-amber-500/20"
        >
          <i className="ri-lock-line" />
          Upgrade — need {TARGET_COUNT} credits (you have {p.balance})
        </a>
      );
    }
    // Jun 15 — product picker now runs as its own wizard step BEFORE
    // /write-concepts (pick-featured-products). The user has already
    // confirmed featured products by the time we reach review. So:
    //   - Zero products available (didn't pick any at brand) → back-nav
    //   - Otherwise → plain Generate CTA
    if (!p.hasProductsAvailable) {
      return (
        <SkipButton onClick={p.onBackToBrand}>
          ← Back to add products
        </SkipButton>
      );
    }
    return (
      <ContinueButton disabled={p.generating} onClick={p.onGenerate}>
        {p.generating ? 'Starting…' : `Generate ${TARGET_COUNT} ads (${TARGET_COUNT} credits) →`}
      </ContinueButton>
    );
  }
  return null;
}

function ContinueButton({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-8 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer flex items-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

// Jun 15 — secondary "Skip" button. Same shape as ContinueButton but
// visually de-emphasized (outlined / muted) since skipping is the
// not-recommended path. Always renders as enabled.
function SkipButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="
        px-8 py-3.5 rounded-full bg-white border-2 border-surface-dark
        text-dark text-sm font-bold hover:bg-surface-dark/20
        transition-all cursor-pointer flex items-center gap-2
      "
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Reusable card chrome (matches /onboarding prototype)
// ---------------------------------------------------------------------------

// Brand imagery (campaign / lifestyle shots) parsed from the homepage on the
// review screen. Shows a few by default with a "Show N more" expander. Jul 11 —
// replaced the old "first 4 product images" preview; the vision-classified
// persist for Brand DNA still runs in the background at Continue.
function BrandImagesCard({
  images, onUpload,
}: {
  images: string[];
  onUpload: (file: File) => Promise<void>;
}) {
  // URLs here are OUR GCS copies (server-verified downloads) — they always
  // load, so no referrerPolicy/onError dance. Sites where nothing could be
  // pulled (WAF CDNs, image-less pages) show the honest empty state + upload.
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || uploading) return;
    setUploading(true);
    setUploadErr(null);
    try {
      for (const f of Array.from(files).slice(0, 6)) await onUpload(f);
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Show every pulled image + the Add cell (they wrap; the card body scrolls
  // if needed). No "+N" overflow — nothing is hidden.
  const shown = images;
  return (
    <Card title="Images">
      <input
        ref={fileRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />
      {images.length === 0 && (
        <p className="text-[10px] text-dark-muted/70 mb-2">
          We couldn't pull imagery from this site — add a few brand photos
          (campaign shots, lifestyle photos) to style your creatives.
        </p>
      )}
      <div
        className="grid grid-cols-3 sm:grid-cols-4 gap-2"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); void handleFiles(e.dataTransfer.files); }}
      >
        {shown.map((url) => (
          <div key={url} className="relative aspect-square rounded bg-surface-dark/40 overflow-hidden">
            <img src={url} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="aspect-square rounded border-2 border-dashed border-dark-muted/30 hover:border-primary/60 hover:bg-primary/5 flex flex-col items-center justify-center gap-0.5 text-dark-muted/70 hover:text-primary transition-colors disabled:opacity-50"
          title="Add brand photos"
        >
          {uploading
            ? <i className="ri-loader-4-line animate-spin text-base" />
            : <i className="ri-add-line text-base" />}
          <span className="text-[9px] font-semibold">{uploading ? 'Adding…' : 'Add'}</span>
        </button>
      </div>
      {uploadErr && <p className="mt-1.5 text-[10px] text-red-500">{uploadErr}</p>}
    </Card>
  );
}

function Card({ title, children, className = '' }: { title?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-xl border border-surface-dark/30 p-4 ${className}`}>
      {title && <div className="text-xs text-dark-muted/70 font-semibold mb-3">{title}</div>}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: URL — first step, big input + helper copy
// ---------------------------------------------------------------------------

function UrlStep({
  websiteUrl, setWebsiteUrl, onSubmit,
}: {
  websiteUrl: string;
  setWebsiteUrl: (v: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="py-16 flex flex-col items-center justify-center gap-4">
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <i className="ri-global-line text-xl" />
        </div>
        <h2 className="font-display font-bold text-dark text-2xl">Your brand website</h2>
      </div>
      <p className="text-sm text-dark-muted text-center max-w-md">
        We'll pull your brand identity, products, and competitors automatically.
      </p>
      <input
        type="url"
        value={websiteUrl}
        onChange={e => setWebsiteUrl(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onSubmit(); }}
        placeholder="https://yourbrand.com"
        autoFocus
        className="w-full max-w-md px-4 py-3 rounded-xl border border-dark-muted/40 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: Competitors intro — Jun 21 Continue/Skip gate. Sits between the
// brand step and find-competitors so the user explicitly opts into the
// ~30s competitor pull (LLM seed → live Meta ads) instead of being
// dropped straight into it. Skip routes to product-only positioning.
// StepFooterCta returns null for this step (default), so the two buttons
// live here in the body.
// ---------------------------------------------------------------------------

// Step 1 of 2 — find competitors. Continue kicks off the LLM competitor
// search (find-competitors → pick-competitors). Skip bails on the whole
// competitor track and positions on the brand's own products.
function CompetitorsIntroStep({
  onContinue, onSkip,
}: {
  onContinue: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="py-16 flex flex-col items-center justify-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
        <i className="ri-search-eye-line text-2xl" />
      </div>
      <h2 className="font-display font-bold text-dark text-2xl text-center">
        Find your competitors?
      </h2>
      <p className="text-sm text-dark-muted text-center max-w-md leading-relaxed">
        We'll suggest brands like yours so you can pick who to study — then we'll
        pull the live Meta ads of the ones you pick. Prefer to skip? We'll
        position you on your own brand and products instead.
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-3 mt-1">
        <SkipButton onClick={onSkip}>Skip for now</SkipButton>
        <ContinueButton onClick={onContinue}>Find competitors →</ContinueButton>
      </div>
    </div>
  );
}

// Step 2 of 2 — pull the picked competitors' live ads. Continue runs the
// Apify scrape (competitor-ads loader). Skip keeps the picks (saved to the
// My Competitors library) but doesn't wait on the ad pull.
function CompetitorAdsIntroStep({
  onContinue, onSkip,
}: {
  onContinue: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="py-16 flex flex-col items-center justify-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
        <i className="ri-radar-line text-2xl" />
      </div>
      <h2 className="font-display font-bold text-dark text-2xl text-center">
        Pull their live ads?
      </h2>
      <p className="text-sm text-dark-muted text-center max-w-md leading-relaxed">
        We'll fetch the Meta ads your picked competitors are running right now
        and use the gap to sharpen your creative direction.
      </p>
      <div className="flex items-center gap-1.5 text-xs text-dark-muted/70 bg-surface-dark/30 rounded-full px-3 py-1.5">
        <i className="ri-time-line" />
        This can take a few minutes — we scrape each competitor's live ads.
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-3 mt-1">
        <SkipButton onClick={onSkip}>Skip for now</SkipButton>
        <ContinueButton onClick={onContinue}>Pull competitor ads →</ContinueButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: Brand Review — 3-column prototype layout, real editing logic
//
// LEFT  : Logo (click to upload/replace) + Fonts (display-only — backend
//         has no typography column yet)
// MID   : Name + URL (inline edit) + Colors (click swatch → native picker,
//         hover for remove, + to add) + Sample images (first 4 products)
// RIGHT : Brief (inline edit) + Products (full grid with selection, search)
// ---------------------------------------------------------------------------

function BrandReviewStep({
  draft, onDraftChange,
  products, productsLoading, productsError, onImportFromUrl, onUploadFile,
  onUploadBrandImage,
  selectedProducts, onToggleProduct,
  productSearch, onProductSearch,
  manualProduct, onManualChange, onManualSubmit, onManualCancel,
}: {
  draft: BrandDraft;
  onDraftChange: (next: BrandDraft) => void;
  products: UserProduct[];
  productsLoading: boolean;
  productsError: string | null;
  onImportFromUrl: (url: string) => void;
  onUploadFile: (file: File) => Promise<void>;
  onUploadBrandImage: (file: File) => Promise<void>;
  selectedProducts: Set<string>;
  onToggleProduct: (id: string) => void;
  productSearch: string;
  onProductSearch: (v: string) => void;
  manualProduct: {
    file: File; previewUrl: string; name: string; description: string;
    saving: boolean; error: string | null;
  } | null;
  onManualChange: (patch: Partial<{ name: string; description: string; error: string | null }>) => void;
  onManualSubmit: () => void;
  onManualCancel: () => void;
}) {
  const name = draft.name;
  const brief = draft.brief;
  const colors = draft.colors;
  const logoUrl = draft.logoBase64;
  // Jun 15 — prefer the substituted Google-Fonts entries when available.
  // Falls back to the raw typography list if the scrape ran before the
  // substitution change (shouldn't happen in practice; defensive).
  const fonts: BrandFontEntry[] = draft.fonts.length > 0
    ? draft.fonts
    : draft.typography.map((family) => ({
        detected: family,
        substitute: family,
        method: 'fallback' as const,
        source: null,
      }));
  const sourceUrl = draft.sourceUrl || '';

  const [editing, setEditing] = useState<'name' | 'brief' | null>(null);
  const [draftName, setDraftName] = useState(name);
  const [draftBrief, setDraftBrief] = useState(brief);

  // Everything below is a pure local-state edit — no backend writes. The
  // single commit happens later in handlePickContinue.
  const commit = (patch: Partial<BrandDraft>) => {
    onDraftChange({ ...draft, ...patch });
    setEditing(null);
  };
  const busy = false;

  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const handleLogoFile = (file: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') commit({ logoBase64: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleColorAdd = () => {
    commit({ colors: [...colors, '#808080'] });
  };
  const handleColorRemove = (idx: number) => {
    commit({ colors: colors.filter((_, i) => i !== idx) });
  };
  const handleColorEdit = (idx: number, v: string) => {
    const next = [...colors];
    next[idx] = v;
    commit({ colors: next });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

      {/* LEFT COLUMN: Logo + Fonts. `contents` on mobile dissolves this
          wrapper so the cards become direct grid children — that lets the
          per-card order-* below reorder them across all 3 columns on mobile.
          md:flex restores the desktop column exactly as before. */}
      <div className="contents md:flex md:flex-col md:gap-4">
        <Card title="Logo" className="order-1 md:order-none h-[150px] md:h-[200px] flex flex-col">
          <button
            type="button"
            onClick={() => logoInputRef.current?.click()}
            disabled={busy}
            className={`
              flex-1 w-full rounded-md transition-colors cursor-pointer
              flex items-center justify-center overflow-hidden border-2
              ${logoUrl
                ? 'border-transparent hover:border-primary/30 bg-white'
                : 'border-dashed border-dark-muted/40 hover:border-primary/40 hover:bg-primary-light/30'}
            `}
            title={logoUrl ? 'Click to replace logo' : 'Click to upload a logo'}
          >
            {logoUrl ? (
              // eslint-disable-next-line jsx-a11y/alt-text
              <img src={logoUrl} className="max-w-full max-h-full object-contain p-2" />
            ) : (
              <span className="px-6 py-3 rounded-md bg-dark text-white text-sm font-semibold hover:bg-dark/90 transition">
                Add Logo
              </span>
            )}
          </button>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleLogoFile(file);
              e.target.value = '';
            }}
            className="hidden"
          />
        </Card>

        <Card title="Fonts" className="order-5 md:order-none">
          {fonts.length > 0 ? (
            <div className="flex gap-3 flex-wrap">
              {fonts.slice(0, 4).map((f, i) => (
                // Jun 15 — Aa swatch + substitute name. Tooltip / italic
                // subtitle surfaces the original site font when the
                // substitution changed something.
                <FontTile
                  key={`${f.detected}-${i}`}
                  label={f.substitute}
                  family={f.substitute}
                  originally={
                    f.detected.toLowerCase() !== f.substitute.toLowerCase()
                      ? f.detected
                      : null
                  }
                  onRemove={() => {
                    const nextFonts = fonts.filter((_, j) => j !== i);
                    onDraftChange({
                      ...draft,
                      fonts: nextFonts,
                      typography: draft.typography.filter((_, j) => j !== i),
                    });
                  }}
                />
              ))}
              {fonts.length > 4 && (
                <div className="text-[10px] text-dark-muted/70 italic self-end">
                  +{fonts.length - 4} more
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-dark-muted/70 italic">No fonts detected</div>
          )}
        </Card>
      </div>

      {/* MIDDLE COLUMN: Name+URL, Colors, Images */}
      <div className="contents md:flex md:flex-col md:gap-4">
        <Card className="order-2 md:order-none">
          <div className="text-center py-3 sm:py-6">
            {editing === 'name' ? (
              <div className="flex flex-col items-center gap-2">
                <input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commit({ name: draftName });
                    if (e.key === 'Escape') setEditing(null);
                  }}
                  className="w-full px-3 py-2 rounded-md border border-dark-muted/40 focus:border-primary outline-none font-display font-bold text-xl text-center"
                  maxLength={120}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => commit({ name: draftName })}
                    disabled={busy}
                    className="px-3 py-1 rounded-md bg-primary text-white text-xs font-semibold disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    className="px-3 py-1 rounded-md text-xs text-dark-muted hover:text-dark"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={() => { setDraftName(name); setEditing('name'); }}
                  className="text-[28px] font-bold text-dark mb-1 hover:text-primary transition-colors block w-full text-center"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                  title="Click to edit name"
                >
                  {name || <span className="text-dark-muted/60 font-normal italic">Brand name</span>}
                </button>
                <div className="text-sm text-dark-muted truncate">
                  {sourceUrl || 'No URL'}
                </div>
              </>
            )}
          </div>
        </Card>

        <Card title="Colors" className="order-4 md:order-none">
          {colors.length > 0 ? (
            // Show the FULL extracted palette (wraps to multiple rows) — each
            // swatch is click-to-recolor + hover-to-remove, with an Add tile
            // below the cap. Nothing hidden behind a "+N". Also editable later
            // in My Brand.
            <div className="flex flex-wrap gap-2">
              {colors.map((hex, i) => {
                return (
                  <div key={i} className="group relative">
                    <label className="cursor-pointer block">
                      <div
                        className="w-16 h-16 rounded-md border border-surface-dark/30 hover:scale-105 transition-transform"
                        style={{ backgroundColor: hex }}
                      />
                      <input
                        type="color"
                        value={/^#[0-9a-f]{6}$/i.test(hex) ? hex : '#888888'}
                        onChange={(e) => handleColorEdit(i, e.target.value)}
                        className="sr-only"
                      />
                    </label>
                    <button
                      onClick={() => handleColorRemove(i)}
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                );
              })}
              {colors.length < 8 && (
                <button
                  onClick={handleColorAdd}
                  disabled={busy}
                  className="w-16 h-16 rounded-md border-2 border-dashed border-dark-muted/40 hover:border-primary/40 hover:text-primary flex items-center justify-center text-dark-muted"
                  title="Add a color"
                >
                  <i className="ri-add-line" />
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handleColorAdd}
              disabled={busy}
              className="w-full min-h-[60px] rounded-md border-2 border-dashed border-dark-muted/40 hover:border-primary/40 hover:text-primary flex items-center justify-center gap-1.5 text-xs text-dark-muted font-semibold"
            >
              <i className="ri-add-line" /> Add color
            </button>
          )}
        </Card>

        <div className="order-7 md:order-none">
          <BrandImagesCard images={draft.images} onUpload={onUploadBrandImage} />
        </div>
      </div>

      {/* RIGHT COLUMN: Brief + Products */}
      <div className="contents md:flex md:flex-col md:gap-4">
        <Card title="Brief" className="order-6 md:order-none">
          {editing === 'brief' ? (
            <div>
              <textarea
                autoFocus
                value={draftBrief}
                onChange={(e) => setDraftBrief(e.target.value)}
                className="w-full px-2 py-1.5 rounded-md border border-dark-muted/40 focus:border-primary outline-none text-xs resize-none"
                rows={6}
                maxLength={1000}
              />
              <div className="mt-2 flex gap-2 justify-end">
                <button onClick={() => setEditing(null)} className="px-2.5 py-1 rounded-md text-xs text-dark-muted hover:text-dark">Cancel</button>
                <button onClick={() => commit({ brief: draftBrief })} disabled={busy} className="px-2.5 py-1 rounded-md bg-primary text-white text-xs font-semibold disabled:opacity-50">Save</button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => { setDraftBrief(brief); setEditing('brief'); }}
              className="text-xs text-dark-muted leading-relaxed line-clamp-6 text-left hover:text-dark transition-colors block w-full"
              title="Click to edit brief"
            >
              {brief || <span className="italic text-dark-muted/60">No brief yet — click to add a one-line business description.</span>}
            </button>
          )}
        </Card>

        <Card title="Products" className="order-3 md:order-none flex-1 flex flex-col">
          {/* Jul 12 — pending manual product: vision couldn't identify the
              uploaded image, so it's NOT saved. Show the image + a required
              name+brief form. Mirrors Product Shot's editable fields. Sits at
              the top of the card so it's the clear next action. */}
          {manualProduct && (
            <ManualProductForm
              product={manualProduct}
              onChange={onManualChange}
              onSubmit={onManualSubmit}
              onCancel={onManualCancel}
            />
          )}
          {/* Selection header + search live inside the Products card so
              the search field doesn't crowd the top-level chrome. */}
          {!productsLoading && products.length > 0 && (
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="text-[10px] text-dark-muted/70">
                {selectedProducts.size}/{products.length} selected
              </div>
              <input
                type="search"
                value={productSearch}
                onChange={(e) => onProductSearch(e.target.value)}
                placeholder="Search"
                className="w-32 px-2 py-1 rounded-md border border-dark-muted/30 text-xs focus:border-primary outline-none"
              />
            </div>
          )}

          <div className="flex-1 min-h-[200px]">
            {productsLoading ? (
              <div className="h-full flex items-center justify-center gap-2 text-xs text-dark-muted">
                <i className="ri-loader-4-line animate-spin text-base" />
                Finding products…
              </div>
            ) : products.length === 0 ? (
              // No auto-found products (discovery failed OR the site has no
              // scrapeable catalog, e.g. KitKat). Never dead-end on a "Retry"
              // that won't work — always offer manual add (import by URL, or
              // skip + add later on the Products page).
              // Aug 1 — gate the empty state on products.length === 0 ONLY (was
              // `productsError || length===0`). A stale/transient brand-step
              // error was blanking the panel to "couldn't auto-find" even with
              // 639 products loaded (Save still counted them → contradiction).
              <AddProductsEmptyState
                onImport={onImportFromUrl}
                onUploadFile={onUploadFile}
                note={productsError ? "We couldn't auto-find products on this site." : undefined}
              />
            ) : (
              <ProductGrid
                products={products}
                selected={selectedProducts}
                onToggle={onToggleProduct}
                search={productSearch}
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// Empty-state for the Products card on the brand step. Primary path is a
// drag/drop image UPLOAD (the WAF-blocked fallback for ae.com / KitKat where
// neither discovery nor URL scraping can reach products — the upload saves a
// permanent My Products row directly). The secondary "add from a store URL"
// link keeps the existing Shopify/Woo import for scrapeable catalogs.
function AddProductsEmptyState({
  onImport,
  onUploadFile,
  note,
}: {
  onImport: (url: string) => void;
  onUploadFile: (file: File) => Promise<void>;
  note?: string;
}) {
  const [mode, setMode] = useState<'idle' | 'link'>('idle');
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const submit = () => {
    const v = url.trim();
    if (!v) return;
    onImport(v);
  };

  const doUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setUploadErr('Please choose an image file.');
      return;
    }
    setUploadErr(null);
    setUploading(true);
    try {
      await onUploadFile(file);
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : 'Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  if (mode === 'link') {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 px-4">
        <p className="text-xs text-dark-muted text-center max-w-[260px]">
          Paste a storefront or collection URL to import products from.
        </p>
        <input
          autoFocus
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') { setMode('idle'); setUrl(''); }
          }}
          placeholder="https://yourbrand.com"
          className="w-full max-w-[280px] px-3 py-2 rounded-md border border-dark-muted/40 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-xs"
        />
        <div className="flex gap-2 mt-1">
          <button
            type="button"
            onClick={() => { setMode('idle'); setUrl(''); }}
            className="px-3 py-1 rounded-md text-xs text-dark-muted hover:text-dark"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!url.trim()}
            className="px-3 py-1 rounded-md bg-primary text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Import
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col items-center justify-center gap-2.5 px-4 text-center">
      {note && (
        <p className="text-[10px] text-dark-muted/80 max-w-[260px]">{note}</p>
      )}
      <div
        role="button"
        tabIndex={0}
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) void doUpload(f);
        }}
        className={`w-full max-w-[300px] rounded-lg border-2 border-dashed px-4 py-7 transition ${
          uploading ? 'cursor-wait' : 'cursor-pointer'
        } ${dragOver ? 'border-primary bg-primary/5' : 'border-dark-muted/40 hover:border-primary/60'}`}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-xs text-dark-muted">
            <i className="ri-loader-4-line animate-spin text-base" /> Saving product…
          </div>
        ) : (
          <>
            <i className="ri-image-add-line text-2xl text-dark-muted/70" />
            <p className="text-xs font-semibold text-dark mt-1.5">Drag &amp; drop a product image</p>
            <p className="text-[10px] text-dark-muted/70 mt-0.5">or click to browse</p>
          </>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void doUpload(f);
          e.target.value = '';
        }}
      />
      {uploadErr && <p className="text-[10px] text-red-600 max-w-[260px]">{uploadErr}</p>}
      <button
        type="button"
        onClick={() => setMode('link')}
        className="text-[11px] text-primary hover:underline"
      >
        Or add products from a store URL →
      </button>
      <p className="text-[10px] text-dark-muted/70 max-w-[250px]">
        Uploaded products are saved to My Products and reusable across posts. Or skip and add later.
      </p>
    </div>
  );
}

function FontTile({
  label,
  family,
  originally,
  onRemove,
}: {
  label: string;
  family: string;
  /** When set, the substitution changed this font — render a tiny italic
   *  caption with the original site font and a tooltip on the swatch. */
  originally?: string | null;
  /** Hover-revealed × button. Optional so callers without a remove
   *  handler render a plain non-removable tile. */
  onRemove?: () => void;
}) {
  return (
    <div className="text-center group relative">
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="
            absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full
            bg-white border border-surface-dark/40 text-dark-muted
            opacity-0 group-hover:opacity-100 transition-opacity
            hover:text-red-500 hover:border-red-500/40
            cursor-pointer flex items-center justify-center z-10 shadow-sm
          "
          title="Remove font"
          aria-label={`Remove ${label}`}
        >
          <i className="ri-close-line text-[11px]" />
        </button>
      )}
      <div
        className="w-14 h-14 rounded-md bg-surface flex items-center justify-center border border-surface-dark/30 text-3xl text-dark"
        style={{ fontFamily: `"${family}", sans-serif` }}
        title={originally ? `${family} (originally: ${originally})` : family}
      >
        Aa
      </div>
      <div
        className="text-xs text-dark-muted mt-1 truncate max-w-[64px]"
        title={label}
      >
        {label}
      </div>
      {originally && (
        <div
          className="text-[9px] text-dark-muted/60 italic truncate max-w-[64px] leading-tight"
          title={`Originally: ${originally}`}
        >
          orig {originally}
        </div>
      )}
    </div>
  );
}

// Jul 12 — inline name+brief form for a manually uploaded product that vision
// couldn't identify. The product is NOT saved yet; we can't advertise a product
// we can't identify (it would leak into a cross-category recipe — e.g. a T-shirt
// in a skincare scene). The user must name + describe it before it's saved.
function ManualProductForm({
  product, onChange, onSubmit, onCancel,
}: {
  product: {
    file: File; previewUrl: string; name: string; description: string;
    saving: boolean; error: string | null;
  };
  onChange: (patch: Partial<{ name: string; description: string; error: string | null }>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const canSave = product.name.trim().length > 0 && product.description.trim().length > 0;
  return (
    <div className="mb-3 rounded-lg border border-primary/40 bg-primary/5 p-3">
      <div className="flex items-start gap-2 mb-2.5">
        <i className="ri-question-answer-line text-primary text-base mt-0.5" />
        <div className="text-xs text-dark">
          <span className="font-semibold">Tell us what this is.</span>{' '}
          <span className="text-dark-muted/80">
            We couldn't identify it from the image — add a name and short brief so we
            know what to advertise. It won't be saved until you do.
          </span>
        </div>
      </div>
      <div className="flex gap-3">
        <img
          src={product.previewUrl}
          alt="Uploaded product"
          className="w-20 h-20 rounded-md object-cover border border-surface-dark/40 flex-shrink-0"
        />
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <input
            type="text"
            value={product.name}
            onChange={(e) => onChange({ name: e.target.value, error: null })}
            placeholder="Product name (e.g. Navy Cotton T-Shirt)"
            disabled={product.saving}
            className="w-full px-2.5 py-1.5 rounded-md border border-dark-muted/30 text-xs focus:border-primary outline-none disabled:opacity-60"
          />
          <textarea
            value={product.description}
            onChange={(e) => onChange({ description: e.target.value, error: null })}
            placeholder="Short description — what it is, material, key feature"
            rows={2}
            disabled={product.saving}
            className="w-full px-2.5 py-1.5 rounded-md border border-dark-muted/30 text-xs focus:border-primary outline-none resize-none disabled:opacity-60"
          />
        </div>
      </div>
      {product.error && (
        <div className="mt-2 text-[11px] text-red-600">{product.error}</div>
      )}
      <div className="flex items-center justify-end gap-2 mt-2.5">
        <button
          onClick={onCancel}
          disabled={product.saving}
          className="px-3 py-1.5 rounded-md text-xs font-medium text-dark-muted hover:text-dark disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          disabled={!canSave || product.saving}
          className="px-3.5 py-1.5 rounded-md text-xs font-semibold bg-primary text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
        >
          {product.saving && <i className="ri-loader-4-line animate-spin" />}
          {product.saving ? 'Saving…' : 'Save product'}
        </button>
      </div>
    </div>
  );
}

// 4-column scrolling product grid that fits inside the Products card.
// Selected = primary border + checkmark; unselected = muted + hover-bright.
function ProductGrid({
  products, selected, onToggle, search,
}: {
  products: UserProduct[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  search: string;
}) {
  const filtered = products.filter(p =>
    !search.trim() || (p.name || '').toLowerCase().includes(search.trim().toLowerCase())
  );
  if (filtered.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-dark-muted">
        No products match "{search}".
      </div>
    );
  }
  return (
    <div className="max-h-[280px] overflow-y-auto">
      <div className="grid grid-cols-3 gap-2">
        {filtered.map(p => {
          const isOn = selected.has(p.id);
          return (
            <button
              key={p.id}
              onClick={() => onToggle(p.id)}
              className={`relative aspect-square rounded overflow-hidden border-2 transition-all ${
                isOn ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
              title={p.name || ''}
            >
              {p.imageUrl ? (
                // NOTE: no loading="lazy" — this grid lives inside the step
                // body's `stepEnter` opacity:0→1 animation. Chrome's lazy
                // IntersectionObserver evaluates while the panel is at
                // opacity:0, defers the load, and never re-triggers → white
                // boxes (CLAUDE.md gotcha #277). Eager load; browser still
                // throttles to ~6 concurrent per host.
                // eslint-disable-next-line jsx-a11y/alt-text
                <img src={p.imageUrl} decoding="async" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-surface" />
              )}
              {isOn && (
                <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center">
                  <i className="ri-check-line text-[10px]" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: Pick Competitors — 2x3 grid of 6 cards (prototype layout)
// ---------------------------------------------------------------------------

function CompetitorsStep({
  suggestions, selected, onToggle,
}: {
  suggestions: CompetitorSuggestion[];
  selected: Set<string>;
  onToggle: (q: string) => void;
}) {
  // Up to 6 visible — matches prototype layout. If we have more, the user
  // can pick from the first 6 (the most-relevant per the LLM ranking).
  // Skip on find-competitors jumps PAST this step to find-opening, so
  // this empty state only fires on a genuine "no results" return.
  const visible = suggestions.slice(0, 9);

  if (visible.length === 0) {
    return (
      <div className="py-20 text-center text-sm text-dark-muted">
        No competitors found. <span className="text-primary cursor-pointer hover:underline">Try again later</span>.
      </div>
    );
  }

  return (
    <div className="lg:flex lg:flex-col lg:h-full">
      <div className="text-sm text-dark-muted mb-4 text-center">
        Pick up to <strong>4</strong> of your closest competitors — {selected.size} selected.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:flex-1 lg:auto-rows-fr">
        {visible.map((c) => {
          const isOn = selected.has(c.query);
          const atCap = !isOn && selected.size >= 4;
          const initial = (c.name?.[0] || '?').toUpperCase();
          return (
            <button
              key={c.query}
              onClick={() => onToggle(c.query)}
              disabled={atCap}
              className={`relative bg-white rounded-xl border-2 p-6 flex flex-col items-center lg:justify-center text-center shadow-sm transition-all ${
                atCap ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
              } ${
                isOn
                  ? 'border-primary shadow-md'
                  : 'border-surface-dark/30 hover:shadow-md hover:border-surface-dark/60'
              }`}
            >
              {/* Logo / initial mark — competitor suggestions don't return
                  a logo URL from the LLM. Show the brand initial in a soft
                  circle as the prototype does. */}
              <div className="h-12 mb-4 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-lg font-bold text-primary">{initial}</span>
                </div>
              </div>
              <div className="text-xl font-bold text-dark">{c.name}</div>
              <div className="text-xs text-dark-muted mt-1 truncate w-full">{c.query}</div>
              {isOn && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center">
                  <i className="ri-check-line text-xs" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: Competitor Ads — per-advertiser section with 3 cards each
// (prototype layout). Real ads where available, placeholder cards when the
// fetch is still pending or returned empty.
// ---------------------------------------------------------------------------

// Meta ad-platform → brand icon. Apify returns lowercased platform keys
// (facebook / instagram / messenger / audience_network / threads). We show the
// user-visible surfaces; audience_network (the off-platform display network)
// has no brand mark, so it renders a small globe.
const _AD_PLATFORM_ICON: Record<string, { icon: string; color: string }> = {
  facebook: { icon: 'ri-facebook-circle-fill', color: '#1877F2' },
  instagram: { icon: 'ri-instagram-line', color: '#E4405F' },
  messenger: { icon: 'ri-messenger-line', color: '#0084FF' },
  threads: { icon: 'ri-at-line', color: '#000000' },
  audience_network: { icon: 'ri-global-line', color: '#8a8a8a' },
};

function AdPlatformIcons({ platforms }: { platforms?: string[] }) {
  const seen = new Set<string>();
  const items = (platforms || [])
    .map((p) => p.toLowerCase().trim())
    .filter((p) => _AD_PLATFORM_ICON[p] && !seen.has(p) && seen.add(p));
  if (items.length === 0) return <span className="w-1" />;
  return (
    <div className="flex items-center gap-1">
      {items.map((p) => {
        const meta = _AD_PLATFORM_ICON[p];
        return <i key={p} className={`${meta.icon} text-sm`} style={{ color: meta.color }} title={p} />;
      })}
    </div>
  );
}

function AdsStep({ bundles }: { bundles: CompetitorAdsBundle[] }) {
  // runLoadAds (parent) already: (a) waited for all competitors,
  // (b) filtered ads where both media_cached_url AND media_ref were
  // falsy, (c) dropped competitors who had 0 image-bearing ads after
  // the filter. So every bundle here has at least one ad and every
  // ad has at least one image URL. No more pending/empty section
  // logic in here — the parent shows WizardLoadingStep instead.
  if (bundles.length === 0) {
    return (
      <div className="py-16 text-center">
        <div className="text-base font-semibold text-dark mb-2">No live ads to show yet</div>
        <div className="text-sm text-dark-muted max-w-md mx-auto">
          None of your picks are running visual Meta ads right now. You can still
          continue — add and manage competitors anytime in the{' '}
          <span className="font-semibold text-dark-muted">My Competitors</span> section.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="mb-1 text-center">
        <h3 className="font-display font-bold text-dark text-base">
          Here are the competitors who are running ads
        </h3>
        <p className="text-[11px] text-dark-muted mt-0.5 max-w-md mx-auto">
          You'll be able to add more competitors and manage them anytime in the{' '}
          <span className="font-semibold text-dark-muted">My Competitors</span> section.
        </p>
      </div>
      {/* The card body (parent) scrolls, so no inner scroll here — a nested
          scroll would trap the wheel. Denser: 6 small ads per row. */}
      <div className="flex flex-col gap-4">
      {bundles.map((bundle) => {
        // headerLabel = bundle.label (set by runLoadAds from the actual
        // scraped advertiser when available), falling back to the query.
        const ads = bundle.ads.slice(0, 6);
        // Honest empty state — this competitor is in the list, we just
        // couldn't pull their live ads in time (site blocked our fast lookup,
        // or they're not running visual ads right now). We keep working on it
        // and the user finds them in /competitors. Never silently drop them.
        if (ads.length === 0) {
          return (
            <div key={bundle.query}>
              <div className="text-xs font-semibold text-dark-muted/80 tracking-wider uppercase bg-surface-dark/40 px-3 py-1.5 rounded-md mb-2">
                {bundle.label || bundle.query} ADS
              </div>
              <div className="rounded-lg border border-dashed border-surface-dark/60 bg-surface/40 px-4 py-3 text-xs text-dark-muted/70 flex items-start gap-2">
                <i className="ri-time-line text-sm text-dark-muted/60 mt-0.5" />
                <span>
                  We need a little more time to pull these ads. They'll appear
                  in your <span className="font-semibold text-dark-muted">Competitors</span> section
                  once ready — no need to wait here.
                </span>
              </div>
            </div>
          );
        }
        return (
          <div key={bundle.query}>
            <div className="text-xs font-semibold text-dark-muted/80 tracking-wider uppercase bg-primary-light/40 px-3 py-1.5 rounded-md mb-2">
              {bundle.label || bundle.query} ADS
            </div>
            <div className="grid grid-cols-6 gap-2">
              {ads.map((ad, i) => {
                // Filter in runLoadAds guarantees one of these is set.
                const url = ad.media_cached_url || ad.media_ref || '';
                const days = ad.longevity_days ?? 0;
                return (
                  <div
                    key={i}
                    className="bg-white rounded-lg border border-surface-dark/30 shadow-sm overflow-hidden flex flex-col"
                  >
                    <div className="aspect-square bg-surface-dark/20">
                      {/* eslint-disable-next-line jsx-a11y/alt-text */}
                      <img src={url} className="w-full h-full object-cover" />
                    </div>
                    {/* Platform icons (where it's running) + how long it's
                        been live — the "this ad is working" signal. Compact
                        for the 6-up dense row. */}
                    <div className="flex items-center justify-between gap-1 px-1.5 py-1 border-t border-surface-dark/20">
                      <AdPlatformIcons platforms={ad.platforms} />
                      {days > 0 && (
                        <span className="text-[9px] text-dark-muted/70 whitespace-nowrap">
                          {days >= 30 ? `${Math.round(days / 30)}mo` : `${days}d`}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Pad to keep 6-column alignment when a competitor has <6. */}
              {Array.from({ length: Math.max(0, 6 - ads.length) }, (_, i) => (
                <div key={`pad-${i}`} className="aspect-square" />
              ))}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step: Review (Winning Ideas) — 3 large 4:5 placeholder cards. Opening
// brief is the LLM gap_analysis from runAdStrategist; rendered below the
// cards as a soft caption.
// ---------------------------------------------------------------------------

const FIVE_VIBES: readonly string[] = [
  'Premium', 'Warm & lifestyle', 'Playful', 'Clean & minimal', 'Editorial',
];

// Jun 15 — Card for a real product-aware winning idea (replaces the
// brand-generic ThemeCard for the new flow). Shows angle name as
// eyebrow + headline + subhead.
function WinningIdeaCard({ idea }: { idea: BrandSetupWinningIdea }) {
  return (
    <div className="bg-primary-light/40 rounded-xl p-5 h-full flex flex-col gap-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-primary/80">
        {idea.angle_name || ' '}
      </div>
      {idea.headline ? (
        <div className="font-display font-bold text-dark text-xl leading-tight">
          {idea.headline}
        </div>
      ) : (
        <div className="py-6 flex items-center justify-center">
          <i className="ri-sparkling-2-line text-primary/30 text-2xl" />
        </div>
      )}
      {idea.subhead && (
        <div className="text-sm text-dark-muted leading-relaxed">
          {idea.subhead}
        </div>
      )}
    </div>
  );
}


function ThemeCard({ theme }: { theme: BrandSetupTheme }) {
  // Card height = its content. No fixed aspect, no flex pushing summary
  // to the bottom — the previous tall-aspect card left a huge empty gap
  // between the headline and the summary. Stack tight: label → headline
  // → summary with consistent small gaps.
  return (
    <div className="bg-primary-light/40 rounded-xl p-5 h-full flex flex-col gap-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-primary/80">
        {theme.vibe}
      </div>
      {theme.headline ? (
        <div className="font-display font-bold text-dark text-xl leading-tight">
          {theme.headline}
        </div>
      ) : (
        <div className="py-6 flex items-center justify-center">
          <i className="ri-sparkling-2-line text-primary/30 text-2xl" />
        </div>
      )}
      {theme.summary && (
        <div className="text-sm text-dark-muted leading-relaxed">
          {theme.summary}
        </div>
      )}
    </div>
  );
}

function WinningIdeasStep({
  opening,
  winningIdeas,
}: {
  opening: string | null;
  winningIdeas: BrandSetupWinningIdea[];
}) {
  // Jun 15 — cards now show REAL concept text (the first concept of
  // each of the 5 angle buckets) instead of brand-generic vibe themes.
  // Each card is the actual ad-copy seed for one of the 5 renders that
  // will fire when the user clicks Generate.
  const slots: BrandSetupWinningIdea[] = winningIdeas.length >= 5
    ? winningIdeas.slice(0, 5)
    : [
        ...winningIdeas,
        ...Array.from({ length: 5 - winningIdeas.length }, () => ({
          angle_name: '',
          headline: '',
          subhead: '',
          visual_direction: '',
        })),
      ];
  return (
    <div>
      <div className="text-base font-semibold text-dark mb-4">
        Your Winning Ideas
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 auto-rows-fr gap-3">
        {slots.map((idea, i) => (
          <WinningIdeaCard key={i} idea={idea} />
        ))}
      </div>
      {opening && (
        <div className="mt-6 px-4 py-3 rounded-lg bg-surface border border-surface-dark/30">
          <div className="text-[10px] uppercase tracking-wider font-bold text-dark-muted/70 mb-1">
            Your opening
          </div>
          <p className="text-sm text-dark leading-relaxed">{opening}</p>
        </div>
      )}
      <div className="mt-4 text-xs text-dark-muted text-center">
        We'll bake {TARGET_COUNT} ads against this brief — 4 per direction.
        They'll appear in My Creatives as each one finishes.
      </div>
    </div>
  );
}


// =============================================================================
// ProductPickerModal — Jun 15
// Opened by the review-step CTA. Searchable grid of the user's
// picked-during-brand products; user toggles which ones to feature in
// the 5 ads. Sub-selection is the wizard's `featuredProductIds` state.
// =============================================================================

// =============================================================================
// PickFeaturedProductsStep — Jun 15
// Renders inside the wizard chrome (vs ProductPickerModal which floats over).
// Step before find-opening so /write-concepts can be called with the picked
// product ids. Live two-way binding with `selectedIds` via onChange.
// =============================================================================

function PickFeaturedProductsStep({
  products,
  selectedIds,
  onChange,
}: {
  products: UserProduct[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const selected = new Set(selectedIds);
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const filtered = q
    ? products.filter(p =>
        (p.name?.toLowerCase().includes(q))
        || (p.description?.toLowerCase().includes(q)),
      )
    : products;

  const atCap = selected.size >= MAX_FEATURED_PRODUCTS;
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (atCap) return; // hard cap — deselect one to add another
      next.add(id);
    }
    onChange(Array.from(next));
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display font-bold text-dark text-lg mb-1">
          Pick the products you want featured
        </h2>
        <p className="text-sm text-dark-muted">
          We'll write {TARGET_COUNT} winning concepts grounded in these
          products. Pick one for tight focus, or up to {MAX_FEATURED_PRODUCTS} to
          cycle across the ads.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products…"
          className="flex-1 px-4 py-2 rounded-full bg-surface border border-surface-dark/40 text-sm text-dark placeholder:text-dark-muted/60 focus:outline-none focus:border-primary/40"
        />
        <span className="text-xs text-dark-muted whitespace-nowrap">
          {selected.size} of {MAX_FEATURED_PRODUCTS}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-dark-muted border border-dashed border-surface-dark/30 rounded-xl">
          No products match — try a different search.
        </div>
      ) : (
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-[420px] overflow-y-auto pr-1">
          {filtered.map((p) => {
            const on = selected.has(p.id);
            const capBlocked = !on && atCap;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                disabled={capBlocked}
                className={`
                  group flex flex-col rounded-lg overflow-hidden border-2 transition-all bg-white
                  ${on
                    ? 'border-primary shadow-md shadow-primary/15 cursor-pointer'
                    : capBlocked
                      ? 'border-surface-dark/30 opacity-40 cursor-not-allowed'
                      : 'border-surface-dark/30 hover:border-surface-dark/60 cursor-pointer'}
                `}
                title={capBlocked
                  ? `Up to ${MAX_FEATURED_PRODUCTS} featured products — deselect one to add another`
                  : (p.name ?? 'Product')}
              >
                <div className={`relative transition-opacity ${on ? '' : 'opacity-70 group-hover:opacity-100'}`}>
                  {p.imageUrl ? (
                    // eslint-disable-next-line jsx-a11y/alt-text
                    <img src={p.imageUrl} className="block w-full aspect-square object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full aspect-square bg-surface flex items-center justify-center text-[10px] text-dark-muted">
                      no img
                    </div>
                  )}
                  {on && (
                    <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow">
                      <i className="ri-check-line text-[12px]" />
                    </div>
                  )}
                </div>
                <div className={`px-1.5 py-1 min-h-[2.4em] text-[10px] leading-tight line-clamp-2 text-center ${on ? 'text-primary font-semibold' : 'text-dark-muted'}`}>
                  {p.name ?? 'Product'}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ProductPickerModal({
  open,
  products,
  initialSelected,
  onConfirm,
  onClose,
}: {
  open: boolean;
  products: UserProduct[];
  initialSelected: string[];
  onConfirm: (ids: string[]) => void;
  onClose: () => void;
}) {
  // Local working selection so users can cancel + revert. Synced on open.
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelected));
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelected));
      setQuery('');
    }
  }, [open, initialSelected]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? products.filter(p =>
        (p.name?.toLowerCase().includes(q))
        || (p.description?.toLowerCase().includes(q)),
      )
    : products;
  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm px-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[85vh] flex flex-col bg-white rounded-2xl shadow-2xl border border-surface-dark/30 overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-surface-dark/20 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-dark text-lg mb-0.5">
              Select products to feature
            </h2>
            <p className="text-xs text-dark-muted">
              Pick the products you want shown across the {TARGET_COUNT} ads.
              We'll cycle through your selection.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full text-dark-muted hover:text-dark hover:bg-surface flex items-center justify-center transition-colors cursor-pointer flex-shrink-0"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Search + count */}
        <div className="px-6 py-3 border-b border-surface-dark/20 flex items-center gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products…"
            className="flex-1 px-4 py-2 rounded-full bg-surface border border-surface-dark/40 text-sm text-dark placeholder:text-dark-muted/60 focus:outline-none focus:border-primary/40"
          />
          <span className="text-xs text-dark-muted whitespace-nowrap">
            {selected.size} of {products.length}
          </span>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-dark-muted">
              No products match — try a different search.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {filtered.map((p) => {
                const on = selected.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggle(p.id)}
                    className={`
                      group flex flex-col rounded-lg overflow-hidden border-2 transition-all cursor-pointer bg-white
                      ${on
                        ? 'border-primary shadow-md shadow-primary/15'
                        : 'border-surface-dark/30 hover:border-surface-dark/60'}
                    `}
                    title={p.name ?? 'Product'}
                  >
                    <div className={`relative transition-opacity ${on ? '' : 'opacity-70 group-hover:opacity-100'}`}>
                      {p.imageUrl ? (
                        // eslint-disable-next-line jsx-a11y/alt-text
                        <img src={p.imageUrl} className="block w-full aspect-square object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full aspect-square bg-surface flex items-center justify-center text-[10px] text-dark-muted">
                          no img
                        </div>
                      )}
                      {on && (
                        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow">
                          <i className="ri-check-line text-[12px]" />
                        </div>
                      )}
                    </div>
                    <div className={`px-1.5 py-1 min-h-[2.4em] text-[11px] leading-tight line-clamp-2 text-center ${on ? 'text-primary font-semibold' : 'text-dark-muted'}`}>
                      {p.name ?? 'Product'}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-surface-dark/20 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-full bg-white border border-surface-dark text-dark text-sm font-bold hover:bg-surface-dark/20 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            disabled={selected.size === 0}
            onClick={() => onConfirm(Array.from(selected))}
            className="
              px-5 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold
              transition-all cursor-pointer shadow-lg shadow-primary/20
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            Select {selected.size > 0 ? `${selected.size} ` : ''}product{selected.size === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}
